import { Select } from "./select";
import { useConfirm } from "./confirm-dialog";
import { Photo } from "./photo";
import { RowMenu } from "./row-menu";
import { TableScroll } from "./table-scroll";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  ArrowUpRight,
  Ban,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Image as ImageIcon,
  ImageOff,
  Info,
  KeyRound,
  Pencil,
  ShieldOff,
  MessageCircle,
  Plus,
  Reply,
  RotateCcw,
  Route,
  Search,
  Send,
  ShieldCheck,
  ShoppingCart,
  TicketPercent,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { apiFetch, apiUrl, AUTH_TOKEN_KEY, uploadImage } from "./lib/auth";
import { Dialog, type DialogProps } from "./dialog";
import { Avatar } from "./avatar";
import { cn } from "@hey404/ui";
import {
  btnPrimary,
  btnSecondary,
  btnDestructive,
  btnDanger,
  inputClass,
  labelClass,
  btnAccent,
  AdminHeading,
  adminErrorClass,
  fieldErrorClass,
  kickerClass,
} from "./admin-ui";
import { timeAgo } from "./lib/time";
import { couponCampaignPath } from "./admin-coupons";

/** Small "i" with a hover/focus tooltip explaining what a section covers. */
/**
 * The ⓘ next to a section title. Click (or Enter/Space) toggles a short
 * explanation right under it; a click outside, Escape or Tab closes it. It
 * used to rely on hover only and its popover had no positioned parent, so it
 * opened off screen and did nothing on touch.
 */
function InfoHint({ text, className }: { text: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const tipId = useId();
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span
      ref={ref}
      className={cn("relative inline-flex shrink-0 align-middle", className)}
    >
      <button
        type="button"
        aria-label="Qué es esto"
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onClick={() => setOpen((value) => !value)}
        onBlur={(event) => {
          if (!ref.current?.contains(event.relatedTarget as Node | null))
            setOpen(false);
        }}
        className={cn(
          "border-ink border-chip grid h-6 w-6 cursor-pointer place-items-center rounded-full transition-colors",
          open ? "bg-ink text-paper" : "bg-paper hover:bg-acid",
        )}
      >
        <Info size={13} aria-hidden="true" />
      </button>
      {open && (
        <span
          id={tipId}
          role="tooltip"
          className="border-ink bg-ink text-paper shadow-brutal border-chip rounded-control absolute left-0 top-full z-30 mt-2 w-64 p-3 text-xs font-medium normal-case leading-snug"
        >
          {text}
        </span>
      )}
    </span>
  );
}
import { AdminSkeleton } from "./loading";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

// The shared input, roomier for these long forms.
const field = cn(inputClass, "mt-2");
const label = labelClass;
// An ISO instant → the `YYYY-MM-DDTHH:mm` local value a datetime-local input
// expects, so editing an event shows its saved time instead of a blank field.
const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const money = (minor: number, currency: string) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(minor / 100);

/** Small square status token, colour-coded by outcome. */
// Status words in Spanish for the badges; unknown values show as they come.
const statusLabels: Record<string, string> = {
  paid: "Pagada",
  pending: "Pendiente",
  failed: "Fallida",
  refunded: "Reembolsada",
  expired: "Expirada",
  active: "Activa",
  past_due: "Pago vencido",
  canceled: "Cancelada",
  cancelled: "Cancelada",
  open: "Abierta",
  draft: "Borrador",
  void: "Anulada",
};

function StatusBadge({ value }: { value: string }) {
  const v = value.toLowerCase();
  const tone = ["paid", "active", "succeeded", "completed"].includes(v)
    ? "bg-acid"
    : ["pending", "past_due", "processing"].includes(v)
      ? "bg-lemon"
      : ["canceled", "cancelled", "failed", "refunded"].includes(v)
        ? "bg-ink text-paper"
        : "bg-bone";
  return (
    <span
      className={`border-ink border-chip inline-block px-2 py-0.5 font-mono text-xs font-bold uppercase ${tone}`}
    >
      {statusLabels[v] ?? value}
    </span>
  );
}

/**
 * Admin data table: title with a count chip, then the shared .data-table look
 * (soft header, airy alternating rows, hover) in a bone card. `numeric`
 * lists the header labels whose column holds money or counts.
 */
function DataTable({
  title,
  count,
  head,
  numeric = [],
  minWidth,
  noun,
  children,
}: {
  title: string;
  count: number;
  /** Plural noun for the "N … · desliza" line when the table scrolls. */
  noun: string;
  head: string[];
  numeric?: string[];
  minWidth: number;
  children: ReactNode;
}) {
  return (
    <article>
      <div className="flex items-center gap-3">
        <h3 className="font-display text-2xl font-extrabold">{title}</h3>
        <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
          {count}
        </span>
      </div>
      <TableScroll count={count} noun={noun} className="mt-3">
        <table className="data-table" style={{ minWidth }}>
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h} className={numeric.includes(h) ? "num" : undefined}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </TableScroll>
    </article>
  );
}

// Admin dialogs share the platform's Dialog shell (dialog.tsx).
function Modal({
  open,
  title,
  onClose,
  children,
  ...rest
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children?: ReactNode;
} & Omit<DialogProps, "open" | "title" | "onClose" | "children">) {
  return (
    <Dialog open={open} title={title} onClose={onClose} {...rest}>
      {children}
    </Dialog>
  );
}
export type AdminSection =
  | "overview"
  | "courses"
  | "learning_tools"
  | "commerce"
  | "coupons"
  | "people"
  | "administrators"
  | "moderation"
  | "audit";
export const adminSectionPaths: Record<AdminSection, string> = {
  overview: "/admin/overview",
  courses: "/admin/courses",
  learning_tools: "/admin/academy",
  commerce: "/admin/commerce",
  coupons: "/admin/coupons",
  people: "/admin/people",
  administrators: "/admin/administrators",
  moderation: "/admin/moderation",
  audit: "/admin/audit",
};
export const adminSectionFromPath = (path: string): AdminSection | null =>
  (Object.entries(adminSectionPaths).find(
    ([, value]) => value === path,
  )?.[0] as AdminSection | undefined) ?? null;
export const adminSections = [
  { id: "overview", label: "Resumen", icon: Activity },
  { id: "courses", label: "Cursos", icon: BookOpen },
  { id: "learning_tools", label: "Academia", icon: BookOpen },
  { id: "commerce", label: "Pagos", icon: ShoppingCart },
  { id: "coupons", label: "Cupones", icon: TicketPercent },
  { id: "people", label: "Personas", icon: Users },
  { id: "administrators", label: "Administradores", icon: ShieldCheck },
  { id: "moderation", label: "Moderación", icon: MessageCircle },
  { id: "audit", label: "Auditoría", icon: Activity },
] satisfies Array<{ id: AdminSection; label: string; icon: typeof Activity }>;

/**
 * Shared with AdminPanel, which prefetches it while its loading shell is up so
 * landing on Resumen paints in one go instead of a second skeleton.
 */
export const overviewQuery = queryOptions({
  queryKey: ["admin", "overview"],
  queryFn: () =>
    apiFetch<{ metrics: Record<string, number> }>("/api/admin/overview"),
});

export function OverviewAdmin() {
  const query = useQuery(overviewQuery);
  if (query.isPending) return <AdminSkeleton variant="overview" />;
  if (!query.data)
    return (
      <p role="alert" className={adminErrorClass}>
        No pudimos cargar el resumen administrativo.
      </p>
    );
  const metrics = query.data.metrics;
  const labels: Record<string, string> = {
    users: "Usuarios",
    courses: "Cursos",
    enrollments: "Inscripciones",
    comments: "Comentarios",
    subscriptions: "Suscripciones",
  };
  return (
    <section>
      <AdminHeading title="Estado del sistema" />
      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Object.entries(metrics).map(([key, value], index) => (
          <div
            key={key}
            className={`border-ink shadow-brutal border-surface p-5 transition-[translate] duration-200 hover:-translate-y-1 ${["bg-acid", "bg-bubble", "bg-lemon", "bg-grape", "bg-bone"][index % 5]} ${["rounded-[3px_18px_6px_6px]", "rounded-[16px_4px_14px_3px]", "rounded-[3px_3px_20px_5px]"][index % 3]}`}
          >
            <span className="block font-mono text-xs font-bold uppercase tracking-[0.06em]">
              {labels[key] ?? key}
            </span>
            <strong className="font-display mt-3 block text-5xl leading-none tracking-[-0.05em]">
              {value}
            </strong>
          </div>
        ))}
      </div>
      <div className="border-ink bg-bone border-surface shadow-brutal mt-7 p-6">
        <h3 className="font-display text-xl font-extrabold">
          Controles operativos
        </h3>
        <p className="text-ink-soft mt-2">
          Usa Personas para administrar accesos, Moderación para revisar
          conversaciones y Auditoría para consultar cambios sensibles.
        </p>
      </div>
    </section>
  );
}

export function CommerceAdmin() {
  const [notice, setNotice] = useState("");
  const query = useQuery({
    queryKey: ["admin", "commerce"],
    queryFn: () =>
      apiFetch<{
        orders: Array<{
          id: string;
          currency: string;
          amountMinor: number;
          subtotalMinor: number | null;
          discountMinor: number;
          discountCode: string | null;
          campaignId: string | null;
          campaignName: string | null;
          status: string;
          provider: string;
          createdAt: string;
        }>;
        subscriptions: Array<{
          id: string;
          interval: string;
          status: string;
          cancelAtPeriodEnd: boolean;
        }>;
        invoices: Array<{
          id: string;
          number: string;
          currency: string;
          amountMinor: number;
          status: string;
        }>;
      }>("/api/admin/commerce"),
  });
  if (query.isPending) return <AdminSkeleton variant="commerce" />;
  if (!query.data)
    return (
      <p role="alert" className={adminErrorClass}>
        No pudimos cargar pagos y suscripciones.
      </p>
    );
  return (
    <section>
      <article className="border-ink bg-bone border-surface shadow-brutal p-5">
        <h3 className="font-display text-xl font-extrabold">
          Asignar suscripción de cortesía
        </h3>
        <p className="text-ink-soft mt-1 text-sm">
          Activa un plan a un usuario sin cobro (soporte, prensa, cortesías). El
          acceso a los cursos por suscripción aplica de inmediato.
        </p>
        {notice && (
          <p className="border-ink bg-acid border-chip mt-4 p-3 text-sm font-bold">
            {notice}
          </p>
        )}
        <form
          className="mt-4 flex flex-wrap items-end gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const values = Object.fromEntries(new FormData(form)) as {
              email: string;
              interval: string;
            };
            setNotice("");
            // Manual fetch (not apiFetch) so we can read the server's error code
            // and show a specific message.
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            const response = await fetch(
              `${apiUrl}/api/admin/commerce/subscriptions`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(values),
              },
            );
            if (response.ok) {
              form.reset();
              await query.refetch();
              setNotice(`Suscripción activada para ${values.email}.`);
              return;
            }
            const data = (await response.json().catch(() => ({}))) as {
              error?: string;
            };
            setNotice(
              data.error === "already_subscribed"
                ? "Ese usuario ya tiene una suscripción activa. Cancela la actual primero."
                : data.error === "user_not_found"
                  ? "No encontramos un usuario con ese email."
                  : "No se pudo activar la suscripción. Revisa los datos.",
            );
          }}
        >
          <label className="min-w-0 flex-1">
            <span className={label}>Email del usuario</span>
            <input
              name="email"
              type="email"
              required
              placeholder="persona@correo.com"
              className={field}
            />
          </label>
          <label>
            <span className={label}>Plan</span>
            <Select name="interval" className={field} defaultValue="monthly">
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
            </Select>
          </label>
          <button className={`${btnPrimary} px-4 py-3`}>
            Activar suscripción
          </button>
        </form>
      </article>
      <div className="mt-6 space-y-6">
        <DataTable
          title="Órdenes"
          noun="órdenes"
          count={query.data.orders.length}
          minWidth={780}
          head={[
            "Fecha",
            "ID",
            "Proveedor",
            "Cupón",
            "Descuento",
            "Total",
            "Estado",
          ]}
          numeric={["Descuento", "Total"]}
        >
          {query.data.orders.length === 0 ? (
            <tr>
              <td colSpan={7} className="empty">
                Sin registros.
              </td>
            </tr>
          ) : (
            query.data.orders.map((row) => (
              <tr key={row.id}>
                <td>{new Date(row.createdAt).toLocaleDateString("es-AR")}</td>
                <td className="code">{row.id.slice(0, 10)}…</td>
                {row.provider === "free" ? (
                  <td>Sin cobro</td>
                ) : (
                  <td className="capitalize">{row.provider}</td>
                )}
                <td>
                  {row.discountCode ? (
                    row.campaignId ? (
                      <a
                        href={couponCampaignPath(row.campaignId)}
                        onClick={(event) => {
                          event.preventDefault();
                          window.history.pushState(
                            {},
                            "",
                            couponCampaignPath(row.campaignId!),
                          );
                          window.dispatchEvent(new PopStateEvent("popstate"));
                          window.scrollTo({ top: 0 });
                        }}
                        title={row.campaignName ?? undefined}
                        className="code font-bold underline"
                      >
                        {row.discountCode}
                      </a>
                    ) : (
                      <span className="code">{row.discountCode}</span>
                    )
                  ) : (
                    <span className="text-ink-soft">—</span>
                  )}
                </td>
                <td className="num">
                  {row.discountMinor > 0
                    ? money(row.discountMinor, row.currency)
                    : "—"}
                </td>
                <td className="num font-bold">
                  {money(row.amountMinor, row.currency)}
                </td>
                <td>
                  <StatusBadge value={row.status} />
                </td>
              </tr>
            ))
          )}
        </DataTable>

        <DataTable
          title="Suscripciones"
          noun="suscripciones"
          count={query.data.subscriptions.length}
          minWidth={520}
          head={["ID", "Plan", "Estado", "Renovación"]}
        >
          {query.data.subscriptions.length === 0 ? (
            <tr>
              <td colSpan={4} className="empty">
                Sin registros.
              </td>
            </tr>
          ) : (
            query.data.subscriptions.map((row) => (
              <tr key={row.id}>
                <td className="code">{row.id.slice(0, 10)}…</td>
                <td>{row.interval === "annual" ? "Anual" : "Mensual"}</td>
                <td>
                  <StatusBadge value={row.status} />
                </td>
                <td className="text-ink-soft">
                  {row.cancelAtPeriodEnd
                    ? "Cancela al fin del período"
                    : "Renueva automáticamente"}
                </td>
              </tr>
            ))
          )}
        </DataTable>

        <DataTable
          title="Facturas"
          noun="facturas"
          count={query.data.invoices.length}
          minWidth={520}
          head={["N°", "ID", "Total", "Estado"]}
          numeric={["Total"]}
        >
          {query.data.invoices.length === 0 ? (
            <tr>
              <td colSpan={4} className="empty">
                Sin registros.
              </td>
            </tr>
          ) : (
            query.data.invoices.map((row) => (
              <tr key={row.id}>
                <td className="code">{row.number}</td>
                <td className="code">{row.id.slice(0, 10)}…</td>
                <td className="num font-bold">
                  {money(row.amountMinor, row.currency)}
                </td>
                <td>
                  <StatusBadge value={row.status} />
                </td>
              </tr>
            ))
          )}
        </DataTable>
      </div>
    </section>
  );
}

type AdminRow = {
  userId: string;
  role: string;
  createdAt?: string;
  user: { name: string; email: string } | null;
};

// What each role can do, in the words the role-change dialog uses.
const roleScopes: Record<string, string> = {
  administrator:
    "Acceso completo: cursos, Academia, pagos, personas, administradores, moderación y auditoría.",
  editor: "Edita cursos y Academia.",
  content_manager:
    "Crea, edita, duplica y elimina cursos, y gestiona Academia.",
  instructor:
    "Trabaja con cursos y Academia, revisa proyectos y modera conversaciones.",
  moderator: "Modera comentarios de clase y foros.",
  support: "Atiende personas, inscripciones y pagos.",
};
const assignableRoles = [
  "administrator",
  "editor",
  "content_manager",
  "instructor",
  "moderator",
  "support",
] as const;
const adminColumns =
  "md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_8rem_2.25rem] md:gap-x-6";

const roleLabels: Record<string, string> = {
  owner: "Propietario",
  administrator: "Administrador",
  editor: "Editor de contenidos",
  content_manager: "Gestor de contenidos",
  instructor: "Profesor",
  moderator: "Moderador",
  support: "Soporte al estudiante",
};
export function AdministratorsAdmin() {
  const [data, setData] = useState<{
    administrators: AdminRow[];
    invitations: Array<{ email: string; role: string }>;
  } | null>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [toRemove, setToRemove] = useState<AdminRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const closeRemove = () => {
    setToRemove(null);
    setRemoveError("");
  };
  const [askConfirm, confirmDialog] = useConfirm();
  // Who is looking: nobody changes their own role, and only an owner touches
  // another owner (the API enforces both; the UI just says so).
  const [me, setMe] = useState<{ userId: string; role: string } | null>(null);
  useEffect(() => {
    apiFetch<{ administrator: { userId: string; role: string } }>(
      "/api/admin/me",
    ).then((result) => setMe(result.administrator));
  }, []);
  const changeRole = async (item: AdminRow, role: string) => {
    if (role === item.role || !item.user) return;
    const first = item.user.name.split(" ")[0];
    const sure = await askConfirm({
      title: `¿Cambiar el rol de ${first} a ${roleLabels[role]}?`,
      message: `${roleScopes[role]} Deja de tener lo que no incluye este rol. El cambio aplica desde su próxima acción en el panel.`,
      detail: `${roleLabels[item.role] ?? item.role} → ${roleLabels[role]}`,
      confirmLabel: "Cambiar rol",
    });
    if (!sure) return;
    try {
      await apiFetch("/api/admin/administrators", {
        method: "POST",
        body: JSON.stringify({ email: item.user.email, role }),
      });
      setNotice(`${item.user.name} ahora es ${roleLabels[role]}.`);
    } catch {
      setNotice("No pudimos cambiar el rol. Intenta de nuevo.");
    }
    await load();
  };
  const revokeInvitation = async (email: string) => {
    const sure = await askConfirm({
      title: "¿Revocar la invitación?",
      message: `${email} ya no recibirá acceso al entrar a hey404. Puedes volver a invitarle cuando quieras.`,
      confirmLabel: "Revocar invitación",
      destructive: true,
    });
    if (!sure) return;
    await apiFetch(
      `/api/admin/administrator-invitations/${encodeURIComponent(email)}`,
      { method: "DELETE" },
    );
    setNotice("Invitación revocada.");
    await load();
  };
  const load = () =>
    apiFetch<{
      administrators: AdminRow[];
      invitations: Array<{ email: string; role: string }>;
    }>("/api/admin/administrators").then(setData);
  useEffect(() => {
    load();
  }, []);
  if (!data) return <AdminSkeleton variant="administrators" />;
  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-2xl font-extrabold">Con acceso</h3>
          <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
            {data.administrators.length}
          </span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className={`${btnPrimary} gap-2 px-4 py-3 text-sm`}
        >
          <UserPlus size={17} /> Dar acceso
        </button>
      </div>
      {notice && (
        <p className="border-ink bg-acid border-chip mt-5 p-3 text-sm">
          {notice}
        </p>
      )}
      <div className="border-ink bg-bone border-chip mt-5">
        <div
          aria-hidden="true"
          className={cn(
            "border-ink/20 text-ink-soft hidden border-b px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em] md:grid",
            adminColumns,
          )}
        >
          <span>Persona</span>
          <span>Rol</span>
          <span>Desde</span>
          <span />
        </div>
        <ul className="divide-ink/15 divide-y">
          {data.administrators.map((item) => {
            const self = item.userId === me?.userId;
            const lockedOwner = item.role === "owner" && me?.role !== "owner";
            const locked = self || item.role === "owner" || !item.user;
            const name = item.user?.name ?? "Administrador";
            return (
              <li
                key={item.userId}
                className={cn(
                  "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-5 py-3.5",
                  adminColumns,
                )}
              >
                <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-3 md:col-start-auto md:row-start-auto">
                  <Avatar
                    name={name}
                    className="border-ink bg-grape h-9 w-9 text-[11px]"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <strong className="person-name text-sm">{name}</strong>
                      {self && (
                        <span className="border-ink bg-lemon border-chip px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-none">
                          Tú
                        </span>
                      )}
                    </div>
                    <p className="text-ink-soft truncate text-sm">
                      {item.user?.email}
                    </p>
                  </div>
                </div>
                <div className="col-span-2 row-start-2 pl-12 md:col-span-1 md:row-start-auto md:pl-0">
                  {locked ? (
                    <span
                      title={
                        self
                          ? "No puedes cambiar tu propio rol."
                          : item.role === "owner"
                            ? "El rol de propietario no se cambia desde el panel."
                            : undefined
                      }
                      className="border-ink bg-paper border-chip inline-flex items-center gap-1.5 px-2 py-1 text-sm"
                    >
                      {item.role === "owner" && (
                        <ShieldCheck size={14} aria-hidden="true" />
                      )}
                      {roleLabels[item.role] ?? item.role}
                    </span>
                  ) : (
                    <Select
                      value={item.role}
                      onChange={(event) => changeRole(item, event.target.value)}
                      aria-label={`Rol de ${name}`}
                      wrapperClassName="max-w-60"
                      className="mt-0 min-h-10 py-2 text-sm"
                    >
                      {assignableRoles.map((role) => (
                        <option key={role} value={role}>
                          {roleLabels[role]}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
                <span className="text-ink-soft hidden whitespace-nowrap font-mono text-xs md:block">
                  {item.createdAt ? shortDate(item.createdAt) : "—"}
                </span>
                <div className="col-start-2 row-start-1 md:col-start-auto md:row-start-auto md:justify-self-end">
                  {self || lockedOwner ? (
                    <span className="block h-9 w-9" aria-hidden="true" />
                  ) : (
                    <RowMenu
                      label={`Acciones para ${name}`}
                      items={[
                        {
                          label: "Quitar acceso",
                          icon: ShieldOff,
                          danger: true,
                          onSelect: () => setToRemove(item),
                        },
                      ]}
                    />
                  )}
                </div>
              </li>
            );
          })}
          {data.invitations.map((item) => (
            <li
              key={item.email}
              className={cn(
                "bg-paper/60 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-5 py-3.5",
                adminColumns,
              )}
            >
              <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-3 md:col-start-auto md:row-start-auto">
                <span
                  aria-hidden="true"
                  className="border-ink/40 text-ink-soft grid h-9 w-9 shrink-0 place-items-center border border-dashed"
                >
                  <UserPlus size={15} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm">{item.email}</p>
                  <p className="text-ink-soft font-mono text-xs">
                    Invitación pendiente
                  </p>
                </div>
              </div>
              <div className="col-span-2 row-start-2 pl-12 md:col-span-1 md:row-start-auto md:pl-0">
                <span className="text-sm">
                  {roleLabels[item.role] ?? item.role}
                </span>
              </div>
              <span className="text-ink-soft hidden font-mono text-xs md:block">
                Al ingresar
              </span>
              <div className="col-start-2 row-start-1 md:col-start-auto md:row-start-auto md:justify-self-end">
                <RowMenu
                  label={`Acciones para la invitación de ${item.email}`}
                  items={[
                    {
                      label: "Revocar invitación",
                      icon: X,
                      danger: true,
                      onSelect: () => revokeInvitation(item.email),
                    },
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-ink-soft mt-3 text-xs">
        Cambia el rol desde la columna «Rol». Nadie puede cambiar su propio rol,
        y el de propietario no se cambia desde el panel.
      </p>
      {confirmDialog}
      <Modal
        open={toRemove !== null}
        onClose={closeRemove}
        kicker="Administradores"
        title="¿Quitar el acceso administrativo?"
        icon={ShieldOff}
        tone="ink"
        size="lg"
        description={
          toRemove?.user?.name
            ? `${toRemove.user.name.split(" ")[0]} dejará de entrar al panel de administración. Su cuenta en hey404 sigue igual.`
            : "Dejará de entrar al panel de administración. Su cuenta en hey404 sigue igual."
        }
        footer={
          <>
            <button
              type="button"
              onClick={closeRemove}
              className={`${btnSecondary} px-4 py-2.5 text-sm`}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={removing}
              onClick={async () => {
                if (!toRemove) return;
                setRemoving(true);
                setRemoveError("");
                try {
                  await apiFetch(
                    `/api/admin/administrators/${toRemove.userId}`,
                    { method: "DELETE" },
                  );
                  setNotice(
                    `${toRemove.user?.name ?? "La persona"} ya no tiene acceso administrativo.`,
                  );
                  closeRemove();
                  await load();
                } catch {
                  setRemoveError(
                    toRemove.role === "owner"
                      ? "Solo otra persona propietaria puede quitar este acceso."
                      : "No pudimos quitar el acceso. No puedes quitarte el tuyo; si es otra persona, intenta de nuevo.",
                  );
                } finally {
                  setRemoving(false);
                }
              }}
              className={`${btnDanger} gap-2 px-4 py-2.5 text-sm`}
            >
              <ShieldOff size={15} aria-hidden="true" />
              {removing ? "Quitando…" : "Quitar acceso"}
            </button>
          </>
        }
      >
        {toRemove && (
          <div className="border-ink bg-bone border-chip rounded-control flex items-center gap-3 p-3">
            <Avatar
              name={toRemove.user?.name ?? "Administrador"}
              className="border-ink bg-grape text-ink h-11 w-11 text-sm"
            />
            <div className="min-w-0 flex-1">
              <p className="person-name truncate">
                {toRemove.user?.name ?? "Administrador"}
              </p>
              <p className="text-ink-soft truncate text-sm">
                <em>{toRemove.user?.email}</em>
              </p>
            </div>
            <span className="border-ink bg-lemon border-chip shrink-0 px-2 py-0.5 font-mono text-xs font-bold">
              {roleLabels[toRemove.role] ?? toRemove.role}
            </span>
          </div>
        )}
        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          <Consequence kind="off">Pierde el acceso al panel</Consequence>
          <Consequence kind="off">
            Pierde sus permisos de{" "}
            {(toRemove && roleLabels[toRemove.role]?.toLowerCase()) ?? "su rol"}
          </Consequence>
          <Consequence kind="keep">
            Conserva su cuenta, cursos y progreso
          </Consequence>
          <Consequence kind="keep">
            Puedes volver a darle acceso cuando quieras
          </Consequence>
        </ul>
        {removeError && (
          <p role="alert" className={cn(fieldErrorClass, "mt-4")}>
            {removeError}
          </p>
        )}
      </Modal>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Dar acceso administrativo"
      >
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            const values = Object.fromEntries(
              new FormData(event.currentTarget),
            );
            const result = await apiFetch<{ status: string }>(
              "/api/admin/administrators",
              { method: "POST", body: JSON.stringify(values) },
            );
            setNotice(
              result.status === "active"
                ? "Acceso actualizado."
                : "Invitación guardada; se activará al ingresar.",
            );
            setOpen(false);
            await load();
          }}
          className="space-y-4"
        >
          <label className={label}>
            Email
            <input
              autoFocus
              name="email"
              type="email"
              required
              className={field}
            />
          </label>
          <label className={label}>
            Rol
            <Select name="role" className={field}>
              <option value="administrator">Administrador</option>
              <option value="editor">Editor de contenidos</option>
              <option value="content_manager">Gestor de contenidos</option>
              <option value="instructor">Profesor</option>
              <option value="moderator">Moderador</option>
              <option value="support">Soporte al estudiante</option>
            </Select>
            <span className="text-ink-soft mt-2 block text-xs normal-case">
              Gestores y profesores trabajan con cursos; moderadores gestionan
              conversaciones; soporte administra personas e inscripciones.
            </span>
          </label>
          <button className={`${btnPrimary} w-full px-4 py-3`}>
            Confirmar acceso
          </button>
        </form>
      </Modal>
    </section>
  );
}

type PersonPlan =
  | {
      kind: "subscription";
      interval: "monthly" | "annual";
      status: "active" | "past_due" | null;
      until: string | null;
      canceling: boolean;
    }
  | { kind: "purchase" }
  | { kind: "none" };
type Person = {
  id: string;
  name: string;
  email: string;
  courseIds: string[];
  banned?: boolean;
  banReason?: string | null;
  createdAt?: string;
  plan?: PersonPlan;
};

// Personas list columns on desktop: persona, cursos, ingreso, plan, ⋮.
const peopleColumns =
  "md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_7.5rem_10rem_2.25rem] md:gap-x-6";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("es", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/** The person's plan as a small tag, with the renewal date on hover. */
function PlanTag({ plan }: { plan?: PersonPlan }) {
  if (!plan || plan.kind === "none")
    return <span className="text-ink-soft font-mono text-xs">Sin plan</span>;
  if (plan.kind === "purchase")
    return (
      <span
        title="Compró al menos un curso"
        className="border-ink bg-paper border-chip px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-none"
      >
        Compra
      </span>
    );
  const label = plan.interval === "annual" ? "Anual" : "Mensual";
  const until = plan.until ? shortDate(plan.until) : null;
  const pastDue = plan.status === "past_due";
  return (
    <span
      title={
        pastDue
          ? "El último cobro falló"
          : until
            ? `${plan.canceling ? "Termina" : "Renueva"} el ${until}`
            : undefined
      }
      className={cn(
        "border-ink border-chip inline-flex flex-col px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-tight",
        pastDue ? "bg-lemon" : plan.canceling ? "bg-paper" : "bg-acid",
      )}
    >
      {pastDue ? `${label} · pago pendiente` : label}
      {plan.canceling && until && (
        <span className="font-normal normal-case">Termina {until}</span>
      )}
    </span>
  );
}
// Who an action is about, at the top of each Personas dialog: initials, the
// name in bold (same face as the text around it), the email in italics and
// how many courses they have.
function PersonCard({ person }: { person: Person }) {
  const courses = person.courseIds.length;
  return (
    <div className="border-ink bg-bone border-chip rounded-control flex items-center gap-3 p-3">
      <Avatar
        name={person.name}
        className="border-ink bg-grape text-ink h-11 w-11 text-sm"
      />
      <div className="min-w-0 flex-1">
        <p className="person-name truncate text-base">{person.name}</p>
        <p className="text-ink-soft truncate text-sm italic">{person.email}</p>
      </div>
      <span className="border-ink bg-paper border-chip shrink-0 px-2 py-0.5 font-mono text-xs font-bold">
        {courses} {courses === 1 ? "curso" : "cursos"}
      </span>
    </div>
  );
}

// One consequence of an action: what stops (✕) or what is kept (✓).
function Consequence({
  kind,
  children,
}: {
  kind: "off" | "keep";
  children: ReactNode;
}) {
  return (
    <li
      className={`border-chip rounded-control flex items-center gap-2 px-3 py-2 text-xs ${kind === "keep" ? "border-ink bg-acid/40" : "border-ink/30 bg-bone border-dashed"}`}
    >
      {kind === "keep" ? (
        <Check
          size={14}
          strokeWidth={3}
          aria-hidden="true"
          className="shrink-0"
        />
      ) : (
        <X size={14} strokeWidth={3} aria-hidden="true" className="shrink-0" />
      )}
      {children}
    </li>
  );
}

export function PeopleAdmin({
  courses,
}: {
  courses: Array<{ id: string; title: string }>;
}) {
  const [users, setUsers] = useState<Person[] | null>(null);
  const [canDeleteUsers, setCanDeleteUsers] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [coursesOf, setCoursesOf] = useState<Person | null>(null);
  const [askConfirm, confirmDialog] = useConfirm();
  const courseTitle = (courseId: string) =>
    courses.find((course) => course.id === courseId)?.title ?? "Curso";
  const unenroll = async (person: Person, courseId: string) => {
    const sure = await askConfirm({
      title: `¿Quitar a ${person.name.split(" ")[0]} de «${courseTitle(courseId)}»?`,
      message:
        "Deja de tener acceso a las clases de este curso. Su progreso se conserva si vuelve a inscribirse.",
      confirmLabel: "Quitar del curso",
      destructive: true,
    });
    if (!sure) return;
    await apiFetch(`/api/admin/enrollments/${courseId}/${person.id}`, {
      method: "DELETE",
    });
    setCoursesOf((current) =>
      current && current.id === person.id
        ? {
            ...current,
            courseIds: current.courseIds.filter((id) => id !== courseId),
          }
        : current,
    );
    await load();
  };
  const [userToDelete, setUserToDelete] = useState<Person | null>(null);
  // Access-recovery / password-reset link the admin can hand to a user.
  const [resetUser, setResetUser] = useState<Person | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [copied, setCopied] = useState(false);
  // The enroll form used to await the request with no catch, so a failure
  // (most often an email with no account: 404 user_not_found) left the modal
  // open and silent. It now explains what went wrong.
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState("");
  const openResetLink = async (user: Person) => {
    setResetUser(user);
    setResetLink(null);
    setResetError(false);
    setCopied(false);
    setResetLoading(true);
    try {
      const result = await apiFetch<{ url: string }>(
        `/api/admin/users/${user.id}/reset-link`,
        { method: "POST" },
      );
      setResetLink(result.url);
    } catch {
      setResetError(true);
    } finally {
      setResetLoading(false);
    }
  };
  const copyResetLink = async () => {
    if (!resetLink) return;
    await navigator.clipboard.writeText(resetLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  // Suspend flow: banning opens a confirm modal (with an optional reason);
  // reactivating is a direct action.
  const [userToBan, setUserToBan] = useState<Person | null>(null);
  const [banReason, setBanReason] = useState("");
  const unbanUser = async (user: Person) => {
    await apiFetch(`/api/admin/users/${user.id}/unban`, { method: "POST" });
    await load();
  };
  const load = (term = search) =>
    apiFetch<{ users: Person[]; canDeleteUsers: boolean }>(
      `/api/admin/users?search=${encodeURIComponent(term)}`,
    ).then((result) => {
      setUsers(result.users);
      setCanDeleteUsers(result.canDeleteUsers);
    });
  useEffect(() => {
    apiFetch<{ users: Person[]; canDeleteUsers: boolean }>(
      "/api/admin/users?search=",
    ).then((result) => {
      setUsers(result.users);
      setCanDeleteUsers(result.canDeleteUsers);
    });
  }, []);
  if (!users) return <AdminSkeleton variant="people" />;
  return (
    <section>
      <div className="flex flex-wrap gap-3">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            load(search);
          }}
          className="flex min-w-[min(100%,18rem)] flex-1 gap-2"
        >
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Buscar personas por nombre o email"
            placeholder="Buscar por nombre o email"
            className={cn(inputClass, "mt-0 min-w-0 flex-1 px-4 py-3")}
          />
          <button className={`${btnSecondary} px-5`}>Buscar</button>
        </form>
        <button
          onClick={() => setOpen(true)}
          className={`${btnPrimary} gap-2 px-4 py-3 text-sm`}
        >
          <Plus size={17} /> Inscribir persona
        </button>
      </div>
      <div className="border-ink bg-bone border-chip mt-5">
        <div
          aria-hidden="true"
          className={cn(
            "border-ink/20 text-ink-soft hidden border-b px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em] md:grid",
            peopleColumns,
          )}
        >
          <span>Persona</span>
          <span>Cursos</span>
          <span>Ingreso</span>
          <span>Plan</span>
          <span />
        </div>
        <ul className="divide-ink/15 divide-y">
          {users.map((user) => (
            <li
              key={user.id}
              className={cn(
                "grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 px-5 py-3.5 md:items-center",
                peopleColumns,
                user.banned && "bg-paper-dim/60",
              )}
            >
              <div className="order-1 flex min-w-0 items-center gap-3">
                <Avatar
                  name={user.name}
                  className={cn(
                    "border-ink h-9 w-9 text-[11px]",
                    user.banned ? "bg-paper" : "bg-grape",
                  )}
                />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <strong className="person-name text-sm">{user.name}</strong>
                    {user.banned && (
                      <span className="border-ink bg-ink text-paper inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-none">
                        <ShieldOff size={11} aria-hidden="true" /> Suspendido
                      </span>
                    )}
                  </div>
                  <p className="text-ink-soft truncate text-sm">{user.email}</p>
                </div>
              </div>
              <div className="order-2 self-center md:order-5 md:justify-self-end">
                <RowMenu
                  label={`Acciones para ${user.name}`}
                  items={[
                    {
                      label: "Ver cursos",
                      icon: BookOpen,
                      onSelect: () => setCoursesOf(user),
                    },
                    {
                      label: "Link de acceso",
                      icon: KeyRound,
                      onSelect: () => openResetLink(user),
                    },
                    user.banned
                      ? {
                          label: "Reactivar cuenta",
                          icon: ShieldCheck,
                          onSelect: () => unbanUser(user),
                        }
                      : {
                          label: "Suspender cuenta",
                          icon: Ban,
                          onSelect: () => {
                            setUserToBan(user);
                            setBanReason("");
                          },
                        },
                    ...(canDeleteUsers
                      ? [
                          {
                            label: "Eliminar cuenta",
                            icon: Trash2,
                            danger: true,
                            onSelect: () => setUserToDelete(user),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
              <div className="order-3 col-span-2 min-w-0 pl-12 md:order-2 md:col-span-1 md:pl-0">
                {user.courseIds.length ? (
                  <button
                    type="button"
                    onClick={() => setCoursesOf(user)}
                    title="Ver y administrar sus cursos"
                    className="group flex max-w-full cursor-pointer items-center gap-2 text-left text-sm"
                  >
                    <span className="truncate group-hover:underline">
                      {courseTitle(user.courseIds[0]!)}
                    </span>
                    {user.courseIds.length > 1 && (
                      <span className="border-ink bg-lemon border-chip shrink-0 px-1.5 py-0.5 font-mono text-[11px] font-bold leading-none">
                        +{user.courseIds.length - 1}
                      </span>
                    )}
                  </button>
                ) : (
                  <span className="text-ink-soft font-mono text-xs">
                    Sin cursos
                  </span>
                )}
              </div>
              <div className="order-4 col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 pl-12 md:contents">
                <span className="text-ink-soft whitespace-nowrap font-mono text-xs md:order-3">
                  <span className="md:hidden">Ingresó </span>
                  {user.createdAt ? shortDate(user.createdAt) : "—"}
                </span>
                <span className="md:order-4">
                  <PlanTag plan={user.plan} />
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {confirmDialog}
      <Modal
        open={Boolean(coursesOf)}
        onClose={() => setCoursesOf(null)}
        title={
          coursesOf ? `Cursos de ${coursesOf.name.split(" ")[0]}` : "Cursos"
        }
        icon={BookOpen}
        tone="acid"
        description="Los cursos a los que tiene acceso. Para sumarle otro, usa «Inscribir persona»."
        footer={
          <button
            type="button"
            onClick={() => setCoursesOf(null)}
            className={`${btnSecondary} px-4 py-2.5 text-sm`}
          >
            Cerrar
          </button>
        }
      >
        {coursesOf && <PersonCard person={coursesOf} />}
        {coursesOf && coursesOf.courseIds.length > 0 ? (
          <ul className="border-ink border-chip rounded-control divide-ink/15 mt-5 divide-y">
            {coursesOf.courseIds.map((courseId) => (
              <li
                key={courseId}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <span className="min-w-0 truncate text-sm">
                  {courseTitle(courseId)}
                </span>
                <button
                  type="button"
                  onClick={() => unenroll(coursesOf, courseId)}
                  className={`${btnSecondary} shrink-0 px-3 py-1.5 text-xs`}
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-soft mt-5 text-sm">No tiene cursos.</p>
        )}
      </Modal>
      <Modal
        open={Boolean(resetUser)}
        onClose={() => setResetUser(null)}
        title="Link de acceso"
        icon={KeyRound}
        tone="lemon"
        description="Un enlace seguro para volver a entrar y elegir una contraseña nueva."
        footer={
          <button
            type="button"
            onClick={() => setResetUser(null)}
            className={`${btnSecondary} px-4 py-2.5 text-sm`}
          >
            Cerrar
          </button>
        }
      >
        {resetUser && <PersonCard person={resetUser} />}
        <ol className="mt-5 space-y-2">
          {[
            "Copia el enlace.",
            "Compártelo por un canal seguro.",
            "La persona elige su propia contraseña e ingresa.",
          ].map((step, index) => (
            <li key={step} className="flex items-start gap-3">
              <span className="border-ink bg-lemon border-chip grid size-6 shrink-0 place-items-center font-mono text-xs font-bold">
                {index + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
        <p className="text-ink-soft mt-4 text-xs italic">
          Vence en <span className="person-name not-italic">1 hora</span>. Tú
          nunca ves la contraseña.
        </p>
        <div className="mt-4">
          {resetLoading && (
            <p className="text-ink-soft font-mono text-xs">Generando enlace…</p>
          )}
          {resetError && (
            <p role="alert" className={fieldErrorClass}>
              No se pudo generar el enlace. Intenta de nuevo.
            </p>
          )}
          {resetLink && (
            <div className="border-ink bg-bone border-chip rounded-control flex items-center gap-2 p-2 pl-3">
              <span className="text-ink-soft min-w-0 flex-1 break-all font-mono text-xs">
                {resetLink}
              </span>
              <button
                type="button"
                onClick={copyResetLink}
                className={`${btnPrimary} min-h-10 shrink-0 gap-1.5 px-3 py-1.5 text-sm`}
              >
                {copied ? (
                  <Check size={14} aria-hidden="true" />
                ) : (
                  <Copy size={14} aria-hidden="true" />
                )}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
          )}
        </div>
      </Modal>
      <Modal
        open={Boolean(userToBan)}
        onClose={() => setUserToBan(null)}
        title="Suspender cuenta"
        icon={Ban}
        tone="lemon"
        description="La cuenta queda en pausa hasta que la reactives."
        footer={
          <>
            <button
              type="button"
              onClick={() => setUserToBan(null)}
              className={`${btnSecondary} px-4 py-2.5 text-sm`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!userToBan) return;
                await apiFetch(`/api/admin/users/${userToBan.id}/ban`, {
                  method: "POST",
                  body: JSON.stringify({ reason: banReason }),
                });
                setUserToBan(null);
                await load();
              }}
              className={`${btnDestructive} gap-2 px-4 py-2.5 text-sm`}
            >
              <Ban size={15} aria-hidden="true" /> Suspender cuenta
            </button>
          </>
        }
      >
        {userToBan && <PersonCard person={userToBan} />}
        <ul className="mt-5 grid gap-2 sm:grid-cols-3">
          <Consequence kind="off">Se cierran sus sesiones</Consequence>
          <Consequence kind="off">No puede ingresar</Consequence>
          <Consequence kind="keep">Conserva datos y progreso</Consequence>
        </ul>
        <label className="mt-5 block">
          <span className={label}>
            Motivo{" "}
            <span className="text-ink-soft font-normal italic">(opcional)</span>
          </span>
          <input
            value={banReason}
            onChange={(event) => setBanReason(event.target.value)}
            placeholder="Ej: pago revertido, uso indebido…"
            className={cn(inputClass, "mt-1.5")}
          />
        </label>
      </Modal>
      <Modal
        open={Boolean(userToDelete)}
        onClose={() => setUserToDelete(null)}
        title="Eliminar usuario"
        icon={Trash2}
        tone="ink"
        description="Borra la cuenta y todo lo que tiene en hey404."
        footer={
          <>
            <button
              type="button"
              onClick={() => setUserToDelete(null)}
              className={`${btnSecondary} px-4 py-2.5 text-sm`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!userToDelete) return;
                await apiFetch(`/api/admin/users/${userToDelete.id}`, {
                  method: "DELETE",
                });
                setUserToDelete(null);
                await load();
              }}
              className={`${btnDanger} gap-2 px-4 py-2.5 text-sm`}
            >
              <Trash2 size={15} aria-hidden="true" /> Eliminar definitivamente
            </button>
          </>
        }
      >
        {userToDelete && <PersonCard person={userToDelete} />}
        <p className="mt-5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
          Se elimina
        </p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {["Inscripciones", "Progreso", "Comentarios", "Datos de cuenta"].map(
            (item) => (
              <li
                key={item}
                className="border-ink bg-bone border-chip rounded-control px-2.5 py-1 text-sm"
              >
                {item}
              </li>
            ),
          )}
        </ul>
        <p className="mt-4 text-sm">
          <em>Esta acción no se puede deshacer.</em>
        </p>
      </Modal>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEnrollError("");
        }}
        title="Inscribir persona"
      >
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            if (enrolling) return;
            const values = Object.fromEntries(
              new FormData(event.currentTarget),
            );
            setEnrolling(true);
            setEnrollError("");
            try {
              await apiFetch("/api/admin/enrollments", {
                method: "POST",
                body: JSON.stringify(values),
              });
            } catch (error) {
              setEnrollError(
                (error as { status?: number }).status === 404
                  ? "No hay ninguna cuenta con ese correo. La persona tiene que crear su cuenta en hey404 antes de que puedas inscribirla."
                  : "No pudimos inscribir a esta persona. Intenta de nuevo.",
              );
              return;
            } finally {
              setEnrolling(false);
            }
            setOpen(false);
            await load();
          }}
          className="space-y-4"
        >
          <label className={label}>
            Email de la cuenta
            <input
              autoFocus
              name="email"
              type="email"
              required
              onChange={() => setEnrollError("")}
              className={field}
            />
          </label>
          <label className={label}>
            Curso
            <Select name="courseId" required className={field}>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </Select>
          </label>
          {enrollError && (
            <p role="alert" className={fieldErrorClass}>
              {enrollError}
            </p>
          )}
          <button
            disabled={enrolling}
            className={`${btnPrimary} w-full px-4 py-3 disabled:opacity-60`}
          >
            {enrolling ? "Inscribiendo…" : "Confirmar inscripción"}
          </button>
        </form>
      </Modal>
    </section>
  );
}

type LearningToolsCatalog = {
  instructors: Array<{
    id: string;
    name: string;
    headline: string;
    bio: string;
    avatarUrl: string | null;
    websiteUrl: string | null;
  }>;
  schools: Array<{
    id: string;
    slug: string;
    title: string;
    description: string;
    imageUrl: string | null;
    websiteUrl: string | null;
    published: boolean;
  }>;
  paths: Array<{
    id: string;
    slug: string;
    title: string;
    description: string;
    objective: string;
    level: "beginner" | "intermediate" | "advanced";
    schoolId: string | null;
    published: boolean;
    courseIds: string[];
  }>;
  events: Array<{
    id: string;
    title: string;
    description: string;
    meetingUrl: string | null;
    startsAt: string;
    endsAt: string | null;
    published: boolean;
    canceled: boolean;
    registrations: number;
  }>;
  submissions: Array<{
    id: string;
    body: string;
    url: string | null;
    status: string;
    feedback: string;
    submittedAt: string | null;
    lessonTitle: string;
    courseTitle: string;
    userName: string | null;
  }>;
  teams: Array<{
    id: string;
    name: string;
    ownerUserId: string;
    members: number;
    paths: string[];
  }>;
  metrics: Record<string, number>;
  capabilities: {
    manageAcademy: boolean;
    reviewProjects: boolean;
    manageTeams: boolean;
  };
};

type TeamMembersData = {
  members: Array<{ userId: string; role: string; name: string; email: string }>;
  invitations: Array<{ email: string; role: string }>;
};

// Manage a team's members: lists current members and pending invites, adds by
// email (existing account → member now, otherwise a pending invite that's
// claimed on their first sign-in), and removes either. Fixes the old add-only
// modal that swallowed errors and showed nothing.
function TeamMembersModal({
  teamId,
  onClose,
}: {
  teamId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<TeamMembersData | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: "ok" | "err";
    text: string;
  } | null>(null);
  const reload = () =>
    apiFetch<TeamMembersData>(
      `/api/admin/learning-tools/teams/${teamId}/members`,
    )
      .then(setData)
      .catch(() => setData({ members: [], invitations: [] }));
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one load per team
  }, [teamId]);
  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const body = Object.fromEntries(new FormData(form));
    setSaving(true);
    setFeedback(null);
    try {
      const res = await apiFetch<{ status?: string }>(
        `/api/admin/learning-tools/teams/${teamId}/members`,
        { method: "POST", body: JSON.stringify(body) },
      );
      form.reset();
      setFeedback({
        kind: "ok",
        text:
          res.status === "invited"
            ? "Invitación enviada — se unirá al equipo cuando se registre con ese email."
            : "Miembro agregado.",
      });
      await reload();
    } catch {
      setFeedback({
        kind: "err",
        text: "No pudimos agregarlo. Revisa el email e intenta de nuevo.",
      });
    } finally {
      setSaving(false);
    }
  };
  const removeMember = async (userId: string) => {
    await apiFetch(
      `/api/admin/learning-tools/teams/${teamId}/members/${userId}`,
      { method: "DELETE" },
    );
    await reload();
  };
  const cancelInvite = async (email: string) => {
    await apiFetch(
      `/api/admin/learning-tools/teams/${teamId}/invitations/${encodeURIComponent(email)}`,
      { method: "DELETE" },
    );
    await reload();
  };
  return (
    <Modal open onClose={onClose} title="Miembros del equipo">
      <div className="space-y-4">
        <div className="space-y-2">
          {data?.members.map((member) => (
            <div
              key={member.userId}
              className="border-ink bg-paper border-chip flex items-center gap-2 p-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">
                  {member.name || member.email}
                </p>
                <p className="text-ink-soft truncate font-mono text-xs">
                  {member.email} · {member.role}
                </p>
              </div>
              {member.role !== "owner" && (
                <button
                  onClick={() => void removeMember(member.userId)}
                  className="border-ink bg-bubble border p-1"
                  aria-label="Quitar miembro"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          {data?.invitations.map((invite) => (
            <div
              key={invite.email}
              className="border-ink bg-lemon border-chip flex items-center gap-2 border-dashed p-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{invite.email}</p>
                <p className="text-ink-soft font-mono text-xs">
                  Pendiente · se une al registrarse · {invite.role}
                </p>
              </div>
              <button
                onClick={() => void cancelInvite(invite.email)}
                className="border-ink bg-bubble border p-1"
                aria-label="Cancelar invitación"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {data && !data.members.length && !data.invitations.length && (
            <p className="text-ink-soft text-sm">Todavía no hay miembros.</p>
          )}
        </div>
        <form
          className="border-ink border-t-(length:--border-chip) space-y-3 pt-4"
          onSubmit={add}
        >
          <label className={label}>
            Email
            <input
              autoFocus
              name="email"
              type="email"
              required
              className={field}
            />
          </label>
          <label className={label}>
            Rol
            <Select name="role" className={field}>
              <option value="learner">Estudiante</option>
              <option value="manager">Manager</option>
            </Select>
          </label>
          {feedback && (
            <p
              className={`border-ink border-chip p-2 text-xs ${feedback.kind === "ok" ? "bg-acid" : "border-l-danger bg-paper border-l-4"}`}
            >
              {feedback.text}
            </p>
          )}
          <button disabled={saving} className={`${btnPrimary} w-full p-3`}>
            {saving ? "Agregando…" : "Agregar miembro"}
          </button>
        </form>
      </div>
    </Modal>
  );
}

// A photo picker that uploads the chosen image to the public academy store and
// keeps the resulting URL in a hidden field, so the surrounding create/edit form
// submits it like any other value. Used for school photos.
function ImageUploadField({
  name,
  caption,
  defaultValue,
}: {
  name: string;
  caption: string;
  defaultValue?: string | null;
}) {
  const [url, setUrl] = useState(defaultValue ?? "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(false);
  // The saved photo no longer loads (deleted or lost file): say so plainly.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const pick = async (file: File) => {
    setUploading(true);
    setError(false);
    try {
      setUrl(await uploadImage(file));
    } catch {
      setError(true);
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-1">
      <span className={label}>{caption}</span>
      <div className="flex items-center gap-3">
        <Photo
          src={url}
          onError={() => setBroken(true)}
          className="border-ink border-chip h-14 w-14 object-cover"
          fallback={
            <div className="border-ink bg-paper text-ink-soft border-chip flex h-14 w-14 items-center justify-center">
              {url ? <ImageOff size={18} /> : <ImageIcon size={18} />}
            </div>
          }
        />
        <label className={`${btnSecondary} cursor-pointer px-3 py-2`}>
          {uploading ? "Subiendo…" : url ? "Cambiar" : "Subir imagen"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void pick(file);
            }}
          />
        </label>
        {url && !uploading && (
          <button
            type="button"
            onClick={() => setUrl("")}
            className="border-ink bg-bubble border p-1"
            aria-label="Quitar imagen"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      {error && (
        <p className={fieldErrorClass}>
          No pudimos subir la imagen. Debe ser una imagen de máx. 2 MB.
        </p>
      )}
      {broken && !error && (
        <p className={fieldErrorClass}>
          La foto guardada no carga. Súbela de nuevo o quítala.
        </p>
      )}
      <input type="hidden" name={name} value={url} />
    </div>
  );
}

// Ordered course picker for a learning path: add from a dropdown, reorder with
// up/down, remove. Renders the chosen ids as hidden `courseIds` inputs IN ORDER,
// so the surrounding form's FormData.getAll("courseIds") returns the sequence —
// the path POST/PATCH persists that order as each course's position.
function OrderedCourseField({
  courses,
  defaultValue = [],
}: {
  courses: Array<{ id: string; title: string }>;
  defaultValue?: string[];
}) {
  const [ids, setIds] = useState<string[]>(defaultValue);
  const titleById = new Map(courses.map((course) => [course.id, course.title]));
  const available = courses.filter((course) => !ids.includes(course.id));
  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setIds(next);
  };
  return (
    <div className="space-y-2">
      <span className={label}>Cursos de la ruta (en orden)</span>
      {ids.length === 0 ? (
        <p className="text-ink-soft text-xs">Todavía no agregaste cursos.</p>
      ) : (
        <ol className="space-y-1">
          {ids.map((id, index) => (
            <li
              key={id}
              className="border-ink bg-paper border-chip flex items-center gap-2 p-2 text-sm"
            >
              <span className="font-mono text-xs font-bold">{index + 1}.</span>
              <span className="min-w-0 flex-1 truncate">
                {titleById.get(id) ?? id}
              </span>
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label="Subir"
                className="border-ink bg-bone border p-1 disabled:opacity-30"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === ids.length - 1}
                aria-label="Bajar"
                className="border-ink bg-bone border p-1 disabled:opacity-30"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => setIds(ids.filter((x) => x !== id))}
                aria-label="Quitar curso"
                className="border-ink bg-bubble border p-1"
              >
                <Trash2 size={14} />
              </button>
              <input type="hidden" name="courseIds" value={id} />
            </li>
          ))}
        </ol>
      )}
      {available.length > 0 && (
        <Select
          value=""
          className={field}
          onChange={(event) => {
            if (event.target.value) setIds([...ids, event.target.value]);
          }}
        >
          <option value="">+ Agregar curso…</option>
          {available.map((course) => (
            <option key={course.id} value={course.id}>
              {course.title}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}

// ── Academia building blocks ────────────────────────────────────────────────
// One panel shape for every catalog list (escuelas, rutas, eventos, equipos):
// title with count and hint, a create action on the right, compact rows with
// a leading tile, a meta line, status tags and icon actions.

// Rows a catalog panel shows before it scrolls inside itself. Past that, a
// search box appears in the panel so long lists stay findable without
// stretching the page.
const PANEL_ROWS = 5;

function AcademyPanel({
  title,
  count,
  hint,
  action,
  empty,
  noun,
  className,
  children,
}: {
  title: string;
  count: number;
  hint: string;
  action?: ReactNode;
  empty: string;
  /** Plural noun for the search placeholder and the "N de M" line. */
  noun: string;
  className?: string;
  /** Renders the rows for the current search query (lowercased). */
  children: (query: string) => ReactNode[];
}) {
  const [query, setQuery] = useState("");
  const [atEnd, setAtEnd] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const long = count > PANEL_ROWS;
  const rows = children(query.trim().toLowerCase());
  const scrolls = rows.length > PANEL_ROWS;
  // The bottom fade says "there's more below" until the list reaches its end.
  const checkEnd = () => {
    const list = listRef.current;
    if (list)
      setAtEnd(list.scrollTop + list.clientHeight >= list.scrollHeight - 4);
  };
  useEffect(checkEnd, [rows.length]);
  return (
    <article
      className={cn(
        "border-ink bg-bone border-surface shadow-brutal flex min-w-0 flex-col",
        className,
      )}
    >
      <header className="border-ink/20 flex flex-wrap items-center gap-3 border-b border-dashed px-5 py-4">
        <h3 className="font-display text-xl font-extrabold">{title}</h3>
        <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
          {count}
        </span>
        <InfoHint text={hint} />
        {action && <div className="ml-auto">{action}</div>}
        {long && (
          <label className="relative flex w-full items-center">
            <Search
              size={14}
              aria-hidden="true"
              className="text-ink-soft pointer-events-none absolute left-3"
            />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                listRef.current?.scrollTo({ top: 0 });
              }}
              aria-label={`Buscar ${noun}`}
              placeholder={`Buscar ${noun}`}
              className={cn(inputClass, "mt-0 min-h-9 py-1.5 pl-9 text-sm")}
            />
          </label>
        )}
      </header>
      {!count ? (
        <p className="text-ink-soft px-5 py-8 text-center text-sm">{empty}</p>
      ) : rows.length === 0 ? (
        <p className="text-ink-soft px-5 py-8 text-center text-sm">
          Nada coincide con «{query.trim()}».
        </p>
      ) : (
        <>
          <ul
            ref={listRef}
            onScroll={checkEnd}
            tabIndex={scrolls ? 0 : undefined}
            aria-label={scrolls ? `Lista de ${noun}` : undefined}
            className={cn(
              "divide-ink/15 divide-y",
              scrolls &&
                "max-h-[23rem] overflow-y-auto overscroll-contain focus-visible:outline-none",
              scrolls && !atEnd && "panel-fade",
            )}
          >
            {rows}
          </ul>
          {scrolls && (
            <p className="border-ink/20 text-ink-soft border-t border-dashed px-5 py-2 font-mono text-xs">
              {query.trim()
                ? `${rows.length} de ${count} ${noun}`
                : `${count} ${noun}`}{" "}
              · desliza para ver más
            </p>
          )}
        </>
      )}
    </article>
  );
}

function AcademyRow({
  leading,
  title,
  meta,
  tags,
  actions,
  muted,
}: {
  leading: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  tags?: ReactNode;
  actions?: ReactNode;
  muted?: boolean;
}) {
  return (
    <li className="flex flex-col gap-3 px-5 py-3.5 sm:flex-row sm:items-center sm:gap-4">
      <div
        className={cn(
          "flex min-w-0 flex-1 items-center gap-4",
          muted && "opacity-55",
        )}
      >
        {leading}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <strong className="font-display min-w-0 font-extrabold">
              {title}
            </strong>
            {tags}
          </div>
          {meta && (
            <p className="text-ink-soft mt-1 truncate font-mono text-xs">
              {meta}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex shrink-0 gap-1.5 pl-14 sm:pl-0">{actions}</div>
      )}
    </li>
  );
}

const AcademyTag = ({
  tone = "bg-paper",
  children,
}: {
  tone?: string;
  children: ReactNode;
}) => (
  <span
    className={`border-ink border-chip px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-none ${tone}`}
  >
    {children}
  </span>
);

const academyTiles = ["bg-acid", "bg-bubble", "bg-lemon", "bg-grape"];
const levelLabels = {
  beginner: "Inicial",
  intermediate: "Intermedio",
  advanced: "Avanzado",
} as const;
const submissionStatus: Record<string, { label: string; tone: string }> = {
  submitted: { label: "Por revisar", tone: "bg-lemon" },
  reviewed: { label: "Revisado", tone: "bg-paper" },
  approved: { label: "Aprobado", tone: "bg-acid" },
  changes_requested: { label: "Cambios pedidos", tone: "bg-bubble" },
  draft: { label: "Borrador", tone: "bg-paper" },
};

/** A project delivery with its inline review (status + feedback). */
function SubmissionRow({
  submission,
  onReviewed,
}: {
  submission: LearningToolsCatalog["submissions"][number];
  onReviewed: (message: string) => Promise<void>;
}) {
  const pending = submission.status === "submitted";
  const [open, setOpen] = useState(false);
  const status = submissionStatus[submission.status] ?? {
    label: submission.status,
    tone: "bg-paper",
  };
  const name = submission.userName ?? "Estudiante";
  return (
    <li className="px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 gap-3">
          <Avatar
            name={name}
            className="border-ink bg-grape h-9 w-9 text-[11px]"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <strong className="font-display font-extrabold">{name}</strong>
              {submission.submittedAt && (
                <span className="text-ink-soft font-mono text-xs">
                  {timeAgo(submission.submittedAt)}
                </span>
              )}
              {/* The panel already says "por revisar"; only reviewed ones tag their result. */}
              {!pending && (
                <AcademyTag tone={status.tone}>{status.label}</AcademyTag>
              )}
            </div>
            <p className="text-ink-soft mt-1 line-clamp-2 text-sm leading-relaxed">
              {submission.body || "Entrega sin descripción."}
            </p>
          </div>
        </div>
        <div className="ml-12 flex shrink-0 flex-wrap gap-2 self-start sm:ml-0">
          {submission.url && (
            <a
              href={submission.url}
              target="_blank"
              rel="noreferrer"
              className={`${btnSecondary} gap-1.5 px-3 py-2 text-xs`}
            >
              Abrir proyecto <ArrowUpRight size={14} aria-hidden="true" />
            </a>
          )}
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className={cn(
              pending ? btnPrimary : btnSecondary,
              "gap-1.5 px-3 py-2 text-xs",
            )}
          >
            <Pencil size={14} aria-hidden="true" />
            {pending ? "Revisar" : "Editar revisión"}
          </button>
        </div>
      </div>
      {open && (
        <form
          className="border-ink/20 mt-4 grid gap-3 border-t border-dashed pt-4 sm:ml-12 sm:grid-cols-[14rem_1fr]"
          onSubmit={async (event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            await apiFetch(
              `/api/admin/learning-tools/submissions/${submission.id}`,
              {
                method: "PATCH",
                body: JSON.stringify({
                  status: data.get("status"),
                  feedback: data.get("feedback"),
                }),
              },
            );
            setOpen(false);
            await onReviewed(`Revisión guardada. Le avisamos a ${name}.`);
          }}
        >
          <label className={labelClass}>
            Resultado
            <Select
              name="status"
              defaultValue={pending ? "approved" : submission.status}
              className={field}
            >
              <option value="approved">Aprobado</option>
              <option value="changes_requested">Pedir cambios</option>
              <option value="reviewed">Revisado</option>
            </Select>
          </label>
          <label className={labelClass}>
            Feedback
            <textarea
              name="feedback"
              required
              rows={3}
              defaultValue={submission.feedback}
              placeholder={`Qué hizo bien ${name} y qué puede mejorar`}
              className={cn(field, "font-sans text-base font-normal")}
            />
          </label>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={`${btnSecondary} px-3 py-2 text-xs`}
            >
              Cancelar
            </button>
            <button className={`${btnPrimary} px-3 py-2 text-xs`}>
              Guardar revisión
            </button>
          </div>
        </form>
      )}
    </li>
  );
}

export function LearningToolsAdmin({
  courses,
}: {
  courses: Array<{ id: string; title: string }>;
}) {
  const queryClient = useQueryClient();
  const [askConfirm, confirmDialog] = useConfirm();
  const [notice, setNotice] = useState("");
  const [showReviewed, setShowReviewed] = useState(false);
  const [createModal, setCreateModal] = useState<
    "school" | "path" | "event" | "team" | null
  >(null);
  const [teamAction, setTeamAction] = useState<{
    teamId: string;
    teamName?: string;
    type: "member" | "path" | "edit";
  } | null>(null);
  const [editModal, setEditModal] = useState<
    | { type: "school"; item: LearningToolsCatalog["schools"][number] }
    | { type: "path"; item: LearningToolsCatalog["paths"][number] }
    | { type: "event"; item: LearningToolsCatalog["events"][number] }
    | null
  >(null);
  const catalogQuery = useQuery({
    queryKey: ["admin", "learning-tools"],
    queryFn: () =>
      apiFetch<LearningToolsCatalog>("/api/admin/learning-tools/catalog"),
  });
  const catalog = catalogQuery.data;
  const load = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "learning-tools"] });
  // What deleting each kind of item takes with it, in plain words.
  const removal = {
    schools: {
      title: (name: string) => `¿Eliminar la escuela «${name}»?`,
      message:
        "Sus rutas no se borran: quedan sin escuela. Los cursos no cambian. Esta acción no se puede deshacer.",
      confirm: "Eliminar escuela",
      done: "Escuela eliminada.",
    },
    paths: {
      title: (name: string) => `¿Eliminar la ruta «${name}»?`,
      message:
        "Los cursos no se borran, pero la ruta desaparece para quienes la seguían y para los equipos que la tenían asignada. Esta acción no se puede deshacer.",
      confirm: "Eliminar ruta",
      done: "Ruta eliminada.",
    },
    events: {
      title: (name: string) => `¿Eliminar el evento «${name}»?`,
      message:
        "Se borra con sus inscripciones y no se avisa a nadie. Si el evento no se va a hacer, mejor cancélalo: así las personas inscritas reciben el aviso.",
      confirm: "Eliminar evento",
      done: "Evento eliminado.",
    },
    teams: {
      title: (name: string) => `¿Eliminar el equipo «${name}»?`,
      message:
        "Se deshace el grupo y sus rutas asignadas. Los miembros conservan sus cuentas, cursos y progreso. Esta acción no se puede deshacer.",
      confirm: "Eliminar equipo",
      done: "Equipo eliminado.",
    },
  } as const;
  const remove = async (
    resource: keyof typeof removal,
    id: string,
    name: string,
  ) => {
    const copy = removal[resource];
    const sure = await askConfirm({
      title: copy.title(name),
      message: copy.message,
      confirmLabel: copy.confirm,
      destructive: true,
    });
    if (!sure) return;
    await apiFetch(`/api/admin/learning-tools/${resource}/${id}`, {
      method: "DELETE",
    });
    setNotice(copy.done);
    await load();
  };
  const togglePublished = async (
    resource: "schools" | "paths" | "events",
    id: string,
    published: boolean,
  ) => {
    await apiFetch(`/api/admin/learning-tools/${resource}/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ published: !published }),
    });
    await load();
  };
  const patch = async (
    resource: string,
    id: string,
    body: Record<string, unknown>,
  ) => {
    await apiFetch(`/api/admin/learning-tools/${resource}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    setNotice("Cambios guardados.");
    setEditModal(null);
    await load();
  };
  const setEventCanceled = async (id: string, canceled: boolean) => {
    await apiFetch(`/api/admin/learning-tools/events/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ canceled }),
    });
    setNotice(canceled ? "Evento cancelado." : "Evento reactivado.");
    await load();
  };
  if (catalogQuery.isPending) return <AdminSkeleton variant="learning" />;
  if (!catalog)
    return (
      <p role="alert" className={adminErrorClass}>
        No pudimos cargar las herramientas de academia.
      </p>
    );
  const canManage = catalog.capabilities.manageAcademy;
  const metricLabels: Record<string, string> = {
    activities: "Actividades registradas",
    quizAttempts: "Intentos de quiz",
    teams: "Equipos",
    projectSubmissions: "Entregas de proyecto",
  };
  const pendingSubmissions = catalog.submissions.filter(
    (submission) => submission.status === "submitted",
  );
  const reviewedSubmissions = catalog.submissions.filter(
    (submission) => submission.status !== "submitted",
  );
  const now = Date.now();
  // Upcoming events first (soonest on top), then past ones (latest first).
  const events = [...catalog.events].sort((a, b) => {
    const aPast = new Date(a.startsAt).getTime() < now;
    const bPast = new Date(b.startsAt).getTime() < now;
    if (aPast !== bPast) return aPast ? 1 : -1;
    return aPast
      ? b.startsAt.localeCompare(a.startsAt)
      : a.startsAt.localeCompare(b.startsAt);
  });
  const createButton = (
    kind: "school" | "path" | "event" | "team",
    text: string,
  ) => (
    <button
      onClick={() => setCreateModal(kind)}
      className={`${btnSecondary} gap-1.5 px-3 py-2 text-xs`}
    >
      <Plus size={14} aria-hidden="true" /> {text}
    </button>
  );
  const publishItem = (
    resource: "schools" | "paths" | "events",
    item: { id: string; published: boolean },
  ) => ({
    label: item.published ? "Ocultar" : "Publicar",
    icon: item.published ? EyeOff : Eye,
    onSelect: () => togglePublished(resource, item.id, item.published),
  });
  return (
    <section>
      {confirmDialog}
      {notice && (
        <p
          role="status"
          className="border-ink bg-acid border-chip mb-6 flex items-center gap-2 p-3 text-sm font-bold"
        >
          <Check size={16} aria-hidden="true" /> {notice}
        </p>
      )}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {Object.entries(catalog.metrics).map(([key, value], index) => (
          <div
            key={key}
            className={`border-ink shadow-brutal border-surface p-5 ${academyTiles[index % academyTiles.length]}`}
          >
            <span className="block font-mono text-xs font-bold uppercase tracking-[0.06em]">
              {metricLabels[key] ?? key}
            </span>
            <strong className="font-display mt-3 block text-5xl leading-none tracking-[-0.05em]">
              {value}
            </strong>
          </div>
        ))}
      </div>

      {catalog.capabilities.reviewProjects && (
        <article className="border-ink bg-bone border-surface shadow-brutal mt-8">
          <header className="border-ink/20 flex flex-wrap items-center gap-3 border-b border-dashed px-5 py-4">
            <h3 className="font-display text-xl font-extrabold">
              Proyectos por revisar
            </h3>
            <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
              {pendingSubmissions.length}
            </span>
            {reviewedSubmissions.length > 0 && (
              <button
                type="button"
                onClick={() => setShowReviewed((value) => !value)}
                aria-expanded={showReviewed}
                className="text-ink-soft hover:text-ink ml-auto font-mono text-xs font-bold underline-offset-4 hover:underline"
              >
                {showReviewed
                  ? "Ocultar revisadas"
                  : `Ver revisadas (${reviewedSubmissions.length})`}
              </button>
            )}
          </header>
          {pendingSubmissions.length === 0 && !showReviewed ? (
            <p className="text-ink-soft px-5 py-8 text-center text-sm">
              No hay entregas esperando revisión.
            </p>
          ) : (
            <div className="divide-ink/15 divide-y">
              {groupBy(
                [
                  ...pendingSubmissions,
                  ...(showReviewed ? reviewedSubmissions : []),
                ],
                (item) => `${item.courseTitle}·${item.lessonTitle}`,
              ).map((group) => (
                <section
                  key={`${group[0].courseTitle}·${group[0].lessonTitle}`}
                >
                  {/* One project header instead of repeating it on every row. */}
                  <header className="bg-paper-dim/50 flex flex-wrap items-baseline gap-x-2 px-5 py-2.5">
                    <h4 className="text-sm font-bold">
                      {group[0].lessonTitle}
                    </h4>
                    <span className="text-ink-soft text-xs">
                      {group[0].courseTitle} · {group.length}{" "}
                      {group.length === 1 ? "entrega" : "entregas"}
                    </span>
                  </header>
                  <ul className="divide-ink/15 divide-y">
                    {group.map((submission) => (
                      <SubmissionRow
                        key={submission.id}
                        submission={submission}
                        onReviewed={async (message) => {
                          setNotice(message);
                          await load();
                        }}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </article>
      )}

      <div className="mt-8 grid items-start gap-6 xl:grid-cols-2">
        <AcademyPanel
          title="Escuelas"
          count={catalog.schools.length}
          hint="Agrupaciones de cursos por temática. Puedes publicarlas como secciones del catálogo."
          empty="Todavía no hay escuelas."
          action={canManage && createButton("school", "Crear escuela")}
          noun="escuelas"
        >
          {(q) =>
            catalog.schools
              .filter((school) => !q || school.title.toLowerCase().includes(q))
              .map((school, index) => {
                const paths = catalog.paths.filter(
                  (path) => path.schoolId === school.id,
                ).length;
                return (
                  <AcademyRow
                    key={school.id}
                    muted={!school.published}
                    leading={
                      <Photo
                        src={school.imageUrl}
                        className="border-ink border-chip h-10 w-10 shrink-0 object-cover"
                        fallback={
                          <span
                            aria-hidden="true"
                            className={`border-ink border-chip font-display grid h-10 w-10 shrink-0 rotate-[-3deg] place-items-center text-sm font-extrabold ${academyTiles[index % academyTiles.length]}`}
                          >
                            {school.title
                              .replace(/^Escuela de /i, "")
                              .slice(0, 2)
                              .toUpperCase()}
                          </span>
                        }
                      />
                    }
                    title={school.title}
                    tags={!school.published && <AcademyTag>Oculta</AcademyTag>}
                    meta={`${paths} ${paths === 1 ? "ruta" : "rutas"} · /${school.slug}`}
                    actions={
                      canManage && (
                        <RowMenu
                          label={`Acciones para ${school.title}`}
                          items={[
                            publishItem("schools", school),
                            {
                              label: "Editar",
                              icon: Pencil,
                              onSelect: () =>
                                setEditModal({ type: "school", item: school }),
                            },
                            {
                              label: "Eliminar escuela",
                              icon: Trash2,
                              danger: true,
                              onSelect: () =>
                                remove("schools", school.id, school.title),
                            },
                          ]}
                        />
                      )
                    }
                  />
                );
              })
          }
        </AcademyPanel>

        <AcademyPanel
          title="Rutas"
          count={catalog.paths.length}
          hint="Secuencias ordenadas de cursos que guían a la persona de principio a fin."
          empty="Todavía no hay rutas."
          action={canManage && createButton("path", "Crear ruta")}
          noun="rutas"
        >
          {(q) =>
            catalog.paths
              .filter((path) => !q || path.title.toLowerCase().includes(q))
              .map((path, index) => {
                const school = catalog.schools.find(
                  (item) => item.id === path.schoolId,
                );
                return (
                  <AcademyRow
                    key={path.id}
                    muted={!path.published}
                    leading={
                      <span
                        aria-hidden="true"
                        className={`border-ink border-chip font-display grid h-10 w-10 shrink-0 place-items-center text-sm font-extrabold ${academyTiles[(index + 1) % academyTiles.length]}`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    }
                    title={path.title}
                    tags={!path.published && <AcademyTag>Oculta</AcademyTag>}
                    meta={[
                      levelLabels[path.level],
                      `${path.courseIds.length} ${path.courseIds.length === 1 ? "curso" : "cursos"}`,
                      school?.title,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    actions={
                      canManage && (
                        <RowMenu
                          label={`Acciones para ${path.title}`}
                          items={[
                            publishItem("paths", path),
                            {
                              label: "Editar",
                              icon: Pencil,
                              onSelect: () =>
                                setEditModal({ type: "path", item: path }),
                            },
                            {
                              label: "Eliminar ruta",
                              icon: Trash2,
                              danger: true,
                              onSelect: () =>
                                remove("paths", path.id, path.title),
                            },
                          ]}
                        />
                      )
                    }
                  />
                );
              })
          }
        </AcademyPanel>

        <AcademyPanel
          title="Eventos en vivo"
          count={catalog.events.length}
          hint="Clases y experiencias en vivo con fecha, que se anuncian a las personas de la academia."
          empty="Todavía no hay eventos."
          action={canManage && createButton("event", "Crear evento")}
          noun="eventos"
        >
          {(q) =>
            events
              .filter((event) => !q || event.title.toLowerCase().includes(q))
              .map((event) => {
                const starts = new Date(event.startsAt);
                const past = starts.getTime() < now;
                return (
                  <AcademyRow
                    key={event.id}
                    muted={past || event.canceled || !event.published}
                    leading={
                      <span
                        aria-hidden="true"
                        className={cn(
                          "border-ink border-chip grid h-11 w-11 shrink-0 place-items-center text-center leading-none",
                          past || event.canceled ? "bg-paper" : "bg-lemon",
                        )}
                      >
                        <span>
                          <span className="font-display block text-lg font-extrabold">
                            {starts.getDate()}
                          </span>
                          <span className="block font-mono text-[10px] font-bold uppercase">
                            {starts
                              .toLocaleDateString("es", { month: "short" })
                              .replace(".", "")}
                          </span>
                        </span>
                      </span>
                    }
                    title={event.title}
                    tags={
                      <>
                        {event.canceled && (
                          <AcademyTag tone="bg-ink text-paper">
                            Cancelado
                          </AcademyTag>
                        )}
                        {!event.canceled && past && (
                          <AcademyTag>Pasado</AcademyTag>
                        )}
                        {!event.published && <AcademyTag>Oculto</AcademyTag>}
                      </>
                    }
                    meta={`${starts.toLocaleDateString("es", { weekday: "long" })} · ${starts.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })} · ${event.registrations} ${event.registrations === 1 ? "inscrita" : "inscritas"}`}
                    actions={
                      canManage && (
                        <RowMenu
                          label={`Acciones para ${event.title}`}
                          items={[
                            publishItem("events", event),
                            {
                              label: event.canceled
                                ? "Reactivar evento"
                                : "Cancelar evento",
                              icon: event.canceled ? RotateCcw : Ban,
                              onSelect: () =>
                                setEventCanceled(event.id, !event.canceled),
                            },
                            {
                              label: "Editar",
                              icon: Pencil,
                              onSelect: () =>
                                setEditModal({ type: "event", item: event }),
                            },
                            {
                              label: "Eliminar evento",
                              icon: Trash2,
                              danger: true,
                              onSelect: () =>
                                remove("events", event.id, event.title),
                            },
                          ]}
                        />
                      )
                    }
                  />
                );
              })
          }
        </AcademyPanel>

        {catalog.capabilities.manageTeams && (
          <AcademyPanel
            title="Equipos"
            count={catalog.teams.length}
            hint="Organizaciones que aprenden juntas: sus miembros y las rutas asignadas con fecha límite."
            empty="Todavía no hay equipos."
            action={createButton("team", "Crear equipo")}
            noun="equipos"
          >
            {(q) =>
              catalog.teams
                .filter((team) => !q || team.name.toLowerCase().includes(q))
                .map((team, index) => (
                  <AcademyRow
                    key={team.id}
                    leading={
                      <Avatar
                        name={team.name}
                        className={`border-ink h-10 w-10 text-xs ${academyTiles[(index + 3) % academyTiles.length]}`}
                      />
                    }
                    title={team.name}
                    meta={[
                      `${team.members} ${team.members === 1 ? "miembro" : "miembros"}`,
                      team.paths.length
                        ? `Ruta: ${team.paths.join(", ")}`
                        : "Sin ruta asignada",
                    ].join(" · ")}
                    actions={
                      <RowMenu
                        label={`Acciones para ${team.name}`}
                        items={[
                          {
                            label: "Miembros",
                            icon: Users,
                            onSelect: () =>
                              setTeamAction({
                                teamId: team.id,
                                type: "member",
                              }),
                          },
                          {
                            label: "Asignar ruta",
                            icon: Route,
                            onSelect: () =>
                              setTeamAction({ teamId: team.id, type: "path" }),
                          },
                          {
                            label: "Editar",
                            icon: Pencil,
                            onSelect: () =>
                              setTeamAction({
                                teamId: team.id,
                                teamName: team.name,
                                type: "edit",
                              }),
                          },
                          {
                            label: "Eliminar equipo",
                            icon: Trash2,
                            danger: true,
                            onSelect: () => remove("teams", team.id, team.name),
                          },
                        ]}
                      />
                    }
                  />
                ))
            }
          </AcademyPanel>
        )}
      </div>
      <Modal
        open={createModal === "school"}
        onClose={() => setCreateModal(null)}
        title="Nueva escuela"
      >
        <form
          className="mt-4 space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            await apiFetch("/api/admin/learning-tools/schools", {
              method: "POST",
              body: JSON.stringify({
                slug: data.get("slug"),
                title: data.get("title"),
                description: data.get("description"),
                imageUrl: data.get("imageUrl") || null,
                websiteUrl: data.get("websiteUrl") || null,
                published: data.get("published") === "on",
              }),
            });
            form.reset();
            setCreateModal(null);
            await load();
          }}
        >
          <input name="title" required placeholder="Nombre" className={field} />
          <input
            name="slug"
            required
            placeholder="nombre-de-la-escuela"
            className={field}
          />
          <textarea
            name="description"
            placeholder="Descripción"
            className={field}
          />
          <ImageUploadField name="imageUrl" caption="Foto" />
          <input
            name="websiteUrl"
            type="url"
            placeholder="Link de sus redes (https://…)"
            className={field}
          />
          <label className={label}>
            <input name="published" type="checkbox" /> Publicada
          </label>
          <button className={`${btnPrimary} w-full p-3`}>Crear escuela</button>
        </form>
      </Modal>
      <Modal
        open={createModal === "path"}
        onClose={() => setCreateModal(null)}
        title="Nueva ruta"
      >
        <form
          className="mt-4 space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            await apiFetch("/api/admin/learning-tools/paths", {
              method: "POST",
              body: JSON.stringify({
                title: data.get("title"),
                slug: data.get("slug"),
                description: data.get("description"),
                objective: data.get("objective"),
                level: data.get("level"),
                schoolId: data.get("schoolId") || null,
                published: data.get("published") === "on",
                courseIds: data.getAll("courseIds"),
              }),
            });
            form.reset();
            setCreateModal(null);
            await load();
          }}
        >
          <input
            name="title"
            required
            placeholder="Nombre de la ruta"
            className={field}
          />
          <input
            name="slug"
            required
            placeholder="nombre-de-la-ruta"
            className={field}
          />
          <textarea
            name="description"
            placeholder="Descripción"
            className={field}
          />
          <input
            name="objective"
            placeholder="Objetivo profesional"
            className={field}
          />
          <Select name="level" className={field}>
            <option value="beginner">Inicial</option>
            <option value="intermediate">Intermedio</option>
            <option value="advanced">Avanzado</option>
          </Select>
          <Select name="schoolId" className={field}>
            <option value="">Sin escuela</option>
            {catalog.schools.map((school) => (
              <option key={school.id} value={school.id}>
                {school.title}
              </option>
            ))}
          </Select>
          <OrderedCourseField courses={courses} />
          <label className={label}>
            <input name="published" type="checkbox" /> Publicada
          </label>
          <button className={`${btnPrimary} w-full p-3`}>Crear ruta</button>
        </form>
      </Modal>
      <Modal
        open={createModal === "event"}
        onClose={() => setCreateModal(null)}
        title="Nuevo evento en vivo"
      >
        <form
          className="mt-4 space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            await apiFetch("/api/admin/learning-tools/events", {
              method: "POST",
              body: JSON.stringify({
                title: data.get("title"),
                description: data.get("description"),
                meetingUrl: data.get("meetingUrl") || null,
                startsAt: data.get("startsAt"),
                endsAt: data.get("endsAt") || null,
                published: data.get("published") === "on",
              }),
            });
            form.reset();
            setCreateModal(null);
            await load();
          }}
        >
          <input name="title" required placeholder="Título" className={field} />
          <textarea
            name="description"
            placeholder="Descripción"
            className={field}
          />
          <input
            name="meetingUrl"
            type="url"
            placeholder="URL de la sala"
            className={field}
          />
          <input
            name="startsAt"
            type="datetime-local"
            required
            className={field}
          />
          <input name="endsAt" type="datetime-local" className={field} />
          <label className={label}>
            <input name="published" type="checkbox" /> Publicado
          </label>
          <button className={`${btnPrimary} w-full p-3`}>Crear evento</button>
        </form>
      </Modal>
      <Modal
        open={createModal === "team"}
        onClose={() => setCreateModal(null)}
        title="Nuevo equipo"
      >
        <form
          className="space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            await apiFetch("/api/admin/learning-tools/teams", {
              method: "POST",
              body: JSON.stringify({
                name: data.get("name"),
                ownerEmail: data.get("ownerEmail"),
              }),
            });
            setCreateModal(null);
            setNotice("Equipo creado.");
            await load();
          }}
        >
          <label className={label}>
            Nombre
            <input autoFocus name="name" required className={field} />
          </label>
          <label className={label}>
            Email de la persona responsable
            <input name="ownerEmail" type="email" required className={field} />
          </label>
          <button className={`${btnPrimary} w-full p-3`}>Crear equipo</button>
        </form>
      </Modal>
      <Modal
        open={teamAction?.type === "edit"}
        onClose={() => setTeamAction(null)}
        title="Editar equipo"
      >
        <form
          key={teamAction?.teamId ?? "none"}
          className="space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!teamAction) return;
            const data = new FormData(event.currentTarget);
            await apiFetch(
              `/api/admin/learning-tools/teams/${teamAction.teamId}`,
              {
                method: "PATCH",
                body: JSON.stringify({ name: data.get("name") }),
              },
            );
            setTeamAction(null);
            setNotice("Equipo actualizado.");
            await load();
          }}
        >
          <label className={label}>
            Nombre
            <input
              autoFocus
              name="name"
              required
              defaultValue={teamAction?.teamName ?? ""}
              className={field}
            />
          </label>
          <button className={`${btnPrimary} w-full p-3`}>
            Guardar cambios
          </button>
        </form>
      </Modal>
      <Modal
        open={teamAction?.type === "path"}
        onClose={() => setTeamAction(null)}
        title="Asignar ruta"
      >
        <form
          className="space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!teamAction) return;
            const data = new FormData(event.currentTarget);
            await apiFetch(
              `/api/admin/learning-tools/teams/${teamAction.teamId}/paths/${String(data.get("pathId"))}`,
              {
                method: "PUT",
                body: JSON.stringify({ dueAt: data.get("dueAt") || null }),
              },
            );
            setTeamAction(null);
            setNotice("Ruta asignada al equipo.");
          }}
        >
          <label className={label}>
            Ruta
            <Select name="pathId" required className={field}>
              {catalog.paths.map((path) => (
                <option key={path.id} value={path.id}>
                  {path.title}
                </option>
              ))}
            </Select>
          </label>
          <label className={label}>
            Fecha límite
            <input name="dueAt" type="datetime-local" className={field} />
          </label>
          <button className={`${btnPrimary} w-full p-3`}>Asignar ruta</button>
        </form>
      </Modal>
      <Modal
        open={editModal?.type === "school"}
        onClose={() => setEditModal(null)}
        title="Editar escuela"
      >
        {editModal?.type === "school" && (
          <form
            key={editModal.item.id}
            className="mt-4 space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              await patch("schools", editModal.item.id, {
                title: data.get("title"),
                description: data.get("description"),
                imageUrl: data.get("imageUrl") || null,
                websiteUrl: data.get("websiteUrl") || null,
                published: data.get("published") === "on",
              });
            }}
          >
            <input
              name="title"
              required
              defaultValue={editModal.item.title}
              placeholder="Nombre"
              className={field}
            />
            <textarea
              name="description"
              defaultValue={editModal.item.description}
              placeholder="Descripción"
              className={field}
            />
            <ImageUploadField
              name="imageUrl"
              caption="Foto"
              defaultValue={editModal.item.imageUrl}
            />
            <input
              name="websiteUrl"
              type="url"
              defaultValue={editModal.item.websiteUrl ?? ""}
              placeholder="Link de sus redes (https://…)"
              className={field}
            />
            <label className={label}>
              <input
                name="published"
                type="checkbox"
                defaultChecked={editModal.item.published}
              />{" "}
              Publicada
            </label>
            <button className={`${btnPrimary} w-full p-3`}>
              Guardar cambios
            </button>
          </form>
        )}
      </Modal>
      <Modal
        open={editModal?.type === "path"}
        onClose={() => setEditModal(null)}
        title="Editar ruta"
      >
        {editModal?.type === "path" && (
          <form
            key={editModal.item.id}
            className="mt-4 space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              await patch("paths", editModal.item.id, {
                title: data.get("title"),
                description: data.get("description"),
                objective: data.get("objective"),
                level: data.get("level"),
                published: data.get("published") === "on",
                courseIds: data.getAll("courseIds"),
              });
            }}
          >
            <input
              name="title"
              required
              defaultValue={editModal.item.title}
              placeholder="Nombre de la ruta"
              className={field}
            />
            <textarea
              name="description"
              defaultValue={editModal.item.description}
              placeholder="Descripción"
              className={field}
            />
            <input
              name="objective"
              defaultValue={editModal.item.objective}
              placeholder="Objetivo profesional"
              className={field}
            />
            <Select
              name="level"
              defaultValue={editModal.item.level}
              className={field}
            >
              <option value="beginner">Inicial</option>
              <option value="intermediate">Intermedio</option>
              <option value="advanced">Avanzado</option>
            </Select>
            <OrderedCourseField
              courses={courses}
              defaultValue={editModal.item.courseIds}
            />
            <label className={label}>
              <input
                name="published"
                type="checkbox"
                defaultChecked={editModal.item.published}
              />{" "}
              Publicada
            </label>
            <button className={`${btnPrimary} w-full p-3`}>
              Guardar cambios
            </button>
          </form>
        )}
      </Modal>
      <Modal
        open={editModal?.type === "event"}
        onClose={() => setEditModal(null)}
        title="Editar evento en vivo"
      >
        {editModal?.type === "event" && (
          <form
            key={editModal.item.id}
            className="mt-4 space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              await patch("events", editModal.item.id, {
                title: data.get("title"),
                description: data.get("description"),
                meetingUrl: data.get("meetingUrl") || null,
                startsAt: data.get("startsAt"),
                endsAt: data.get("endsAt") || null,
                published: data.get("published") === "on",
              });
            }}
          >
            <input
              name="title"
              required
              defaultValue={editModal.item.title}
              placeholder="Título"
              className={field}
            />
            <textarea
              name="description"
              defaultValue={editModal.item.description}
              placeholder="Descripción"
              className={field}
            />
            <input
              name="meetingUrl"
              type="url"
              defaultValue={editModal.item.meetingUrl ?? ""}
              placeholder="URL de la sala"
              className={field}
            />
            <input
              name="startsAt"
              type="datetime-local"
              required
              defaultValue={toLocalInput(editModal.item.startsAt)}
              className={field}
            />
            <input
              name="endsAt"
              type="datetime-local"
              defaultValue={toLocalInput(editModal.item.endsAt)}
              className={field}
            />
            <label className={label}>
              <input
                name="published"
                type="checkbox"
                defaultChecked={editModal.item.published}
              />{" "}
              Publicado
            </label>
            <button className={`${btnPrimary} w-full p-3`}>
              Guardar cambios
            </button>
          </form>
        )}
      </Modal>
      {teamAction?.type === "member" && (
        <TeamMembersModal
          teamId={teamAction.teamId}
          onClose={() => setTeamAction(null)}
        />
      )}
    </section>
  );
}

type ModerationComment = {
  id: string;
  userName: string;
  body: string;
  question: boolean;
  answered: boolean;
  hidden: boolean;
  parentId: string | null;
  parentUserName: string | null;
  createdAt: string;
  lessonId: string;
  lessonTitle: string;
  lessonPosition: number;
  moduleId: string;
  moduleTitle: string;
  modulePosition: number;
  courseId: string;
  courseTitle: string;
  courseSlug: string;
};
type ForumPending = {
  id: string;
  courseId: string;
  courseTitle: string | null;
  userName: string;
  parentId: string | null;
  threadTitle: string | null;
  title: string | null;
  body: string;
  createdAt: string;
};
type CommentFilter = "all" | "visible" | "hidden" | "unanswered";

/** Groups items by key, keeping first-seen order. */
function groupBy<T>(items: T[], key: (item: T) => string) {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const id = key(item);
    groups.set(id, [...(groups.get(id) ?? []), item]);
  }
  return [...groups.values()] as [T, ...T[]][];
}

const CountChip = ({ children }: { children: ReactNode }) => (
  <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
    {children}
  </span>
);

const Tag = ({
  tone = "bg-paper",
  children,
}: {
  tone?: string;
  children: ReactNode;
}) => (
  <span
    className={`border-ink border-chip px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase leading-none ${tone}`}
  >
    {children}
  </span>
);

/** Who wrote it and when, shared by forum posts and comments. */
function AuthorLine({
  name,
  createdAt,
  tone,
  children,
}: {
  name: string;
  createdAt: string;
  tone: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Avatar
        name={name}
        className={`border-ink h-8 w-8 text-[11px] ${tone}`}
      />
      <strong className="person-name text-sm">{name}</strong>
      {children}
      <time
        dateTime={createdAt}
        title={new Date(createdAt).toLocaleString("es-AR")}
        className="text-ink-soft font-mono text-xs"
      >
        · {timeAgo(createdAt)}
      </time>
    </div>
  );
}

function ModerationEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="border-ink/30 bg-bone/60 border-chip flex items-center gap-4 border-dashed p-6">
      <span className="border-ink bg-acid border-chip grid h-11 w-11 shrink-0 rotate-[-4deg] place-items-center">
        <Check size={20} aria-hidden="true" />
      </span>
      <div>
        <p className="font-display text-lg font-extrabold">Todo al día</p>
        <p className="text-ink-soft text-sm">{children}</p>
      </div>
    </div>
  );
}

/**
 * One lesson comment in the moderation queue: author line, body and its
 * actions. "Responder" opens an inline reply right under it, so the admin
 * answers without leaving the queue; the class link covers the full context.
 */
function ModerationCommentRow({
  comment,
  busy,
  onToggleHidden,
  onReply,
}: {
  comment: ModerationComment;
  busy: boolean;
  onToggleHidden: () => void;
  onReply: (body: string) => Promise<void>;
}) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const isQuestion = comment.question && !comment.parentId;
  // Replies go to the thread's root; name who will read it.
  const recipient = comment.parentId
    ? (comment.parentUserName ?? "la persona")
    : comment.userName;
  async function send(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      await onReply(text.trim());
      setText("");
      setReplying(false);
    } catch {
      setError("No pudimos publicar la respuesta. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  }
  return (
    <li
      className={cn(
        "px-5 py-4",
        comment.parentId && "sm:pl-12",
        comment.hidden && "bg-paper-dim/60",
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className={cn("min-w-0 flex-1", comment.hidden && "opacity-60")}>
          <AuthorLine
            name={comment.userName}
            createdAt={comment.createdAt}
            tone={comment.parentId ? "bg-acid" : "bg-grape"}
          >
            {isQuestion &&
              (comment.answered ? (
                <Tag>Respondida</Tag>
              ) : (
                <Tag tone="bg-lemon">Sin responder</Tag>
              ))}
            {comment.parentId && (
              <span className="text-ink-soft text-xs">
                respondió a {comment.parentUserName ?? "un comentario"}
              </span>
            )}
            {comment.hidden && <Tag tone="bg-ink text-paper">Oculto</Tag>}
          </AuthorLine>
          <p className="text-ink-soft mt-2 line-clamp-3 pl-10 text-sm">
            {comment.body}
          </p>
        </div>
        <div className="ml-10 flex shrink-0 gap-2 self-start sm:ml-0">
          {!comment.hidden && (
            <button
              type="button"
              aria-expanded={replying}
              onClick={() => setReplying((value) => !value)}
              className={cn(
                isQuestion && !comment.answered ? btnPrimary : btnSecondary,
                "gap-1.5 px-3 py-2 text-xs",
              )}
            >
              <Reply size={14} aria-hidden="true" /> Responder
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={onToggleHidden}
            className={cn(
              comment.hidden ? btnAccent : btnSecondary,
              "gap-1.5 px-3 py-2 text-xs",
            )}
          >
            {comment.hidden ? (
              <>
                <Eye size={14} aria-hidden="true" /> Restaurar
              </>
            ) : (
              <>
                <EyeOff size={14} aria-hidden="true" /> Ocultar
              </>
            )}
          </button>
        </div>
      </div>
      {replying && (
        <form onSubmit={send} className="mt-4 pl-10">
          <textarea
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setReplying(false);
            }}
            rows={3}
            aria-label={`Responder a ${recipient}`}
            placeholder={`Escribe tu respuesta para ${recipient}`}
            className={cn(inputClass, "mt-0 resize-y")}
          />
          {error && (
            <p role="alert" className={cn(fieldErrorClass, "mt-2")}>
              {error}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-ink-soft text-xs">
              Se publica en la clase con tu nombre y le avisamos a {recipient}.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReplying(false)}
                className={`${btnSecondary} px-3 py-2 text-xs`}
              >
                Cancelar
              </button>
              <button
                disabled={sending || !text.trim()}
                className={`${btnPrimary} gap-1.5 px-3 py-2 text-xs`}
              >
                <Send size={14} aria-hidden="true" />
                {sending ? "Publicando…" : "Publicar respuesta"}
              </button>
            </div>
          </div>
        </form>
      )}
    </li>
  );
}

export function ModerationAdmin() {
  const [comments, setComments] = useState<ModerationComment[] | null>(null);
  const [forum, setForum] = useState<ForumPending[] | null>(null);
  const [tab, setTab] = useState<"forum" | "comments">("forum");
  const [courseId, setCourseId] = useState("");
  const [filter, setFilter] = useState<CommentFilter>("all");
  // Lessons the admin folded; everything starts open.
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState("");
  const load = () =>
    Promise.all([
      apiFetch<{ comments: ModerationComment[] }>("/api/admin/moderation").then(
        (result) => setComments(result.comments),
      ),
      apiFetch<{ posts: ForumPending[] }>("/api/admin/moderation/forum").then(
        (result) => setForum(result.posts),
      ),
    ]);
  useEffect(() => {
    load();
  }, []);
  async function act(id: string, path: string, body: object) {
    setBusy(id);
    try {
      await apiFetch(path, { method: "PATCH", body: JSON.stringify(body) });
      await load();
    } finally {
      setBusy("");
    }
  }
  async function reply(id: string, body: string) {
    await apiFetch(`/api/admin/moderation/${id}/reply`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
    await load();
  }
  if (!comments || !forum) return <AdminSkeleton variant="moderation" />;

  const courseOptions = [
    ...new Map([
      ...comments.map((c) => [c.courseId, c.courseTitle] as const),
      ...forum.map((p) => [p.courseId, p.courseTitle ?? "Sin curso"] as const),
    ]),
  ].sort((a, b) => a[1].localeCompare(b[1], "es"));
  const inCourse = <T extends { courseId: string }>(items: T[]) =>
    courseId ? items.filter((item) => item.courseId === courseId) : items;
  const pendingForum = inCourse(forum);
  const courseComments = inCourse(comments);
  const shownComments = courseComments
    .filter((c) =>
      filter === "visible"
        ? !c.hidden
        : filter === "hidden"
          ? c.hidden
          : filter === "unanswered"
            ? c.question && !c.answered && !c.parentId
            : true,
    )
    .sort(
      (a, b) =>
        a.courseTitle.localeCompare(b.courseTitle, "es") ||
        a.modulePosition - b.modulePosition ||
        a.lessonPosition - b.lessonPosition ||
        b.createdAt.localeCompare(a.createdAt),
    );
  // Within a lesson, replies follow the comment they answer (oldest first)
  // instead of floating above it by date.
  const threaded = shownComments.filter(
    (c) => !c.parentId || !shownComments.some((p) => p.id === c.parentId),
  );
  for (const reply of [...shownComments]
    .filter((c) => c.parentId && threaded.every((t) => t.id !== c.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    const parent = threaded.findIndex((c) => c.id === reply.parentId);
    let at = parent + 1;
    while (threaded[at]?.parentId === reply.parentId) at++;
    threaded.splice(at, 0, reply);
  }
  const hiddenCount = courseComments.filter((c) => c.hidden).length;

  const tabs = [
    {
      id: "forum",
      label: "Foro",
      more: " por aprobar",
      count: pendingForum.length,
    },
    {
      id: "comments",
      label: "Comentarios",
      more: " de clase",
      count: courseComments.length,
    },
  ] as const;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div
          role="tablist"
          aria-label="Qué moderar"
          className="border-ink bg-paper border-chip rounded-control flex w-full gap-1 p-1 sm:inline-flex sm:w-auto"
        >
          {tabs.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`moderation-tab-${item.id}`}
              aria-selected={tab === item.id}
              aria-controls="moderation-panel"
              tabIndex={tab === item.id ? 0 : -1}
              onClick={() => setTab(item.id)}
              onKeyDown={(event) => {
                if (event.key !== "ArrowRight" && event.key !== "ArrowLeft")
                  return;
                event.preventDefault();
                const next = tabs[(index + 1) % tabs.length]!;
                setTab(next.id);
                document.getElementById(`moderation-tab-${next.id}`)?.focus();
              }}
              className={cn(
                "flex min-h-10 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[6px] px-3 text-sm transition-colors sm:flex-none sm:px-4",
                tab === item.id ? "bg-ink text-paper" : "hover:bg-paper-dim",
              )}
            >
              <span>
                {item.label}
                <span className="hidden sm:inline">{item.more}</span>
              </span>
              <span
                className={cn(
                  "min-w-6 rounded-full px-1.5 py-0.5 text-center font-mono text-xs font-bold",
                  tab === item.id ? "bg-lemon text-ink" : "bg-ink/10",
                )}
              >
                {item.count}
              </span>
            </button>
          ))}
        </div>
        <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto">
          <Select
            value={courseId}
            onChange={(event) => setCourseId(event.target.value)}
            aria-label="Filtrar por curso"
            wrapperClassName={
              tab === "comments"
                ? "sm:w-auto sm:min-w-56"
                : "col-span-2 sm:w-auto sm:min-w-56"
            }
            className="mt-0"
          >
            <option value="">Todos los cursos</option>
            {courseOptions.map(([id, title]) => (
              <option key={id} value={id}>
                {title}
              </option>
            ))}
          </Select>
          {tab === "comments" && (
            <Select
              value={filter}
              onChange={(event) =>
                setFilter(event.target.value as CommentFilter)
              }
              aria-label="Filtrar comentarios"
              wrapperClassName="sm:w-auto sm:min-w-44"
              className="mt-0"
            >
              <option value="all">Todos</option>
              <option value="visible">Visibles</option>
              <option value="hidden">Ocultos ({hiddenCount})</option>
              <option value="unanswered">Sin responder</option>
            </Select>
          )}
        </div>
      </div>

      <div
        id="moderation-panel"
        role="tabpanel"
        aria-labelledby={`moderation-tab-${tab}`}
        className="mt-8"
      >
        {tab === "forum" &&
          (pendingForum.length === 0 ? (
            <ModerationEmpty>
              No hay posts del foro esperando aprobación.
            </ModerationEmpty>
          ) : (
            <div className="space-y-10">
              {groupBy(pendingForum, (p) => p.courseId).map((posts) => (
                <div key={posts[0].courseId}>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-2xl font-extrabold">
                      {posts[0].courseTitle ?? "Sin curso"}
                    </h3>
                    <CountChip>{posts.length}</CountChip>
                  </div>
                  <ul className="border-ink bg-bone border-chip divide-ink/15 mt-4 divide-y">
                    {posts.map((post) => (
                      <li
                        key={post.id}
                        className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start"
                      >
                        <div className="min-w-0 flex-1">
                          <AuthorLine
                            name={post.userName}
                            createdAt={post.createdAt}
                            tone="bg-grape"
                          >
                            <Tag tone={post.parentId ? "bg-paper" : "bg-lemon"}>
                              {post.parentId ? "Respuesta" : "Hilo nuevo"}
                            </Tag>
                          </AuthorLine>
                          <div className="mt-3 pl-10">
                            {post.parentId && post.threadTitle && (
                              <p className="text-ink-soft text-xs">
                                En «{post.threadTitle}»
                              </p>
                            )}
                            {post.title && (
                              <p className="font-display font-extrabold">
                                {post.title}
                              </p>
                            )}
                            <p className="text-ink-soft mt-1 line-clamp-3 text-sm">
                              {post.body}
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 gap-2 pl-10 sm:pl-0">
                          <button
                            disabled={busy === post.id}
                            onClick={() =>
                              act(
                                post.id,
                                `/api/admin/moderation/forum/${post.id}`,
                                { approved: true },
                              )
                            }
                            className={`${btnAccent} gap-1.5 px-3 py-2 text-xs`}
                          >
                            <Check size={14} aria-hidden="true" /> Aprobar
                          </button>
                          <button
                            disabled={busy === post.id}
                            onClick={() =>
                              act(
                                post.id,
                                `/api/admin/moderation/forum/${post.id}`,
                                { hidden: true },
                              )
                            }
                            className={`${btnSecondary} gap-1.5 px-3 py-2 text-xs`}
                          >
                            <X size={14} aria-hidden="true" /> Rechazar
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}

        {tab === "comments" &&
          (shownComments.length === 0 ? (
            <ModerationEmpty>
              {filter === "all"
                ? "Todavía no hay comentarios en las clases."
                : "Ningún comentario coincide con este filtro."}
            </ModerationEmpty>
          ) : (
            <div className="space-y-12">
              {groupBy(threaded, (c) => c.courseId).map((course) => (
                <div key={course[0].courseId}>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-2xl font-extrabold">
                      {course[0].courseTitle}
                    </h3>
                    <CountChip>{course.length}</CountChip>
                  </div>
                  <div className="mt-5 space-y-7">
                    {groupBy(course, (c) => c.moduleId).map((module) => (
                      <div key={module[0].moduleId}>
                        <p className={`${kickerClass} text-ink-soft text-xs`}>
                          Módulo {module[0].modulePosition + 1} ·{" "}
                          {module[0].moduleTitle}
                        </p>
                        <div className="mt-3 space-y-3">
                          {groupBy(module, (c) => c.lessonId).map((lesson) => {
                            const id = lesson[0].lessonId;
                            const open = !folded.has(id);
                            const unanswered = lesson.filter(
                              (c) => c.question && !c.answered && !c.parentId,
                            ).length;
                            const hidden = lesson.filter(
                              (c) => c.hidden,
                            ).length;
                            return (
                              <article
                                key={id}
                                className="border-ink bg-bone border-chip"
                              >
                                <button
                                  type="button"
                                  aria-expanded={open}
                                  aria-controls={`lesson-comments-${id}`}
                                  onClick={() =>
                                    setFolded((current) => {
                                      const next = new Set(current);
                                      if (open) next.add(id);
                                      else next.delete(id);
                                      return next;
                                    })
                                  }
                                  className="hover:bg-paper-dim/60 flex w-full cursor-pointer items-start gap-3 px-5 py-4 text-left transition-colors"
                                >
                                  <span className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                    <span className="min-w-0">
                                      <span className="text-ink-soft mr-2 font-mono text-xs font-bold">
                                        Clase {lesson[0].lessonPosition + 1}
                                      </span>
                                      <span className="font-display font-extrabold">
                                        {lesson[0].lessonTitle}
                                      </span>
                                    </span>
                                    <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                                      {unanswered > 0 && (
                                        <Tag tone="bg-lemon">
                                          {unanswered} sin responder
                                        </Tag>
                                      )}
                                      {hidden > 0 && (
                                        <Tag tone="bg-ink text-paper">
                                          {hidden}{" "}
                                          {hidden === 1 ? "oculto" : "ocultos"}
                                        </Tag>
                                      )}
                                      <span className="text-ink-soft font-mono text-xs">
                                        {lesson.length}{" "}
                                        {lesson.length === 1
                                          ? "comentario"
                                          : "comentarios"}
                                      </span>
                                    </span>
                                  </span>
                                  <ChevronDown
                                    size={16}
                                    aria-hidden="true"
                                    className={cn(
                                      "mt-1 shrink-0 transition-transform duration-200 sm:mt-0.5",
                                      open && "rotate-180",
                                    )}
                                  />
                                </button>
                                {open && (
                                  <ul
                                    id={`lesson-comments-${id}`}
                                    className="border-ink/15 divide-ink/15 divide-y border-t border-dashed"
                                  >
                                    {lesson.map((comment) => (
                                      <ModerationCommentRow
                                        key={comment.id}
                                        comment={comment}
                                        busy={busy === comment.id}
                                        onToggleHidden={() =>
                                          act(
                                            comment.id,
                                            `/api/admin/moderation/${comment.id}`,
                                            {
                                              hidden: !comment.hidden,
                                            },
                                          )
                                        }
                                        onReply={(body) =>
                                          reply(comment.id, body)
                                        }
                                      />
                                    ))}
                                  </ul>
                                )}
                                {open && (
                                  <div className="border-ink/15 flex justify-end border-t border-dashed px-5 py-3">
                                    <a
                                      href={`/courses/${lesson[0].courseSlug}?lesson=${id}#comentarios`}
                                      className="font-display inline-flex items-center gap-1.5 text-sm font-bold hover:underline"
                                    >
                                      Abrir la clase
                                      <ArrowUpRight
                                        size={15}
                                        aria-hidden="true"
                                      />
                                    </a>
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </section>
  );
}
